import { gql } from 'apollo-angular';

export const USER_PARTS = gql`
  fragment UserParts on EmailUserType {
    id
    created
    email
    firstName
    lastName
    isRepresentative
    image
  }
`;

export const CASE_PARTS = gql`
  fragment CaseParts on ApplicationCaseType {
    id
    created
    caseSubType
    caseCode
    caseStatus
    isEditableByCustomer
    isReadonlyForCaseworker
    caseTeam
    deadlineDate
    deadlineStatus
    originalEstimatedFinishDate
    currentFinishDate
    dryRunDate
    highLevelMarker
    highLevelNumber
    archivedReason
    userInviteSent
    expirationDate
    outcomeStatus
    pausedSince
    prePausedDeadlineDate
  }
`;

export const APPLICANT_PARTS = gql`
  fragment ApplicantParts on ApplicantType {
    id
    firstName
    lastName
    fullName
  }
`;

export const COMPANY_PARTS = gql`
  fragment CompanyParts on CompanyType {
    id
    created
    modified
    name
    logo
    companyLogo
    permissionGroupId
    allowedPermissions
    applicantCount
    applicationcaseCount
    recruiterPassword
  }
`;

export const MY_COMPANIES_QUERY = gql`
  query Companies {
    myCompanies {
      id
      name
      applicationcaseCount
      applicantCount
      allowedPermissions
    }
  }
`;

export const BUSINESS_UNIT_PARTS = gql`
  fragment BusinessUnitParts on BusinessUnitType {
    id
    name
  }
`;

export const BUSINESS_UNIT_GROUP_MEMBERSHIP_PARTS = gql`
  fragment BusinessUnitGroupMembershipParts on BusinessUnitGroupMembershipType {
    id
    businessUnit {
      id
      name
    }
    group {
      id
      name
    }
  }
`;

export const JOB_TEMPLATE_PARTS = gql`
  fragment JobTemplateParts on JobTemplateType {
    jobTitle
    department
    socCode
    socName
    jobDuties
    jobDescription
    ukSalary1
    ukSalary2
    requirementsInfo
    departmentInfo
  }
`;

export const COMPANY_LOCATION_PARTS = gql`
  fragment CompanyLocationParts on CompanyLocationType {
    id
    name
    streetNumber
    street
    aptType
    aptNumber
    city
    stateProvince
    postalCode
    country
    locationType
    addressSummary
    phoneNumber
  }
`;

export const NOTE_PARTS = gql`
  fragment NoteParts on NoteType {
    id
    noteType
    applicationCase {
      ...CaseParts
    }
    applicant {
      ...ApplicantParts
    }
    company {
      ...CompanyParts
    }
    text
    created
    userBy {
      email
      firstName
      lastName
    }
  }
  ${CASE_PARTS}
  ${APPLICANT_PARTS}
  ${COMPANY_PARTS}
`;

export const CASE_MARKER_PARTS = gql`
  fragment CaseMarkerParts on CaseMarkerType {
    id
    name
    description
    startedBy
    finishedBy
    startedDate
    finishedDate
    timelineFinishedDate
    deadline
    action
    actionButtonData
    actionData
    isActionAvailable
    level
    order
    hasChildren
    timeline
    isVisible
    parentName
    startedBillingData
    finishedBillingData
    isMilestone
  }
`;

export const STEPQ_PARTS = gql`
  fragment StepQParts on StepQType {
    tabTitle
    tabId
    tabqId
    tabOrder
    guestEnabled
    guestOnly
    many
    required
    startPrompt
    editPrompt
    prompt
    questionnaireText
    singleXml
    questionnaireNames
    questionnaireIds
    questionnaireReltypeIds
    questionnaireReltypeTypes
    tabqOrder
    managerEditable
    hasResponse
    responseId
    completed
  }
`;

export const COMPANY_QUERY = gql`
  query company($companyId: ID, $companyName: String) {
    company(id: $companyId, name: $companyName) {
      ...CompanyParts
      companyComponents {
        id
        name
        status
        sets
        shortName
        useRelation
        subtitle
        visibilityRules
        docType
        specificTo
        componentId
        required
        component {
          id
          name
          associatedData
          type
          instructionImage
          instructionHtml
        }
        uploadedDocument {
          id
          created
          modified
          file
          fileName
          language
          needsTranslation
          translatedFile
          translatedFileName
          translatedFileUsePlaceholder
          ownedBy
        }
        uploadedDocuments {
          id
          created
          modified
          file
          fileName
          language
          needsTranslation
          translatedFile
          translatedFileName
          translatedFileUsePlaceholder
          ownedBy
        }
        hideFromApplicant
        uniqueTogetherId
        includeInManifest
        allowPrivate
        allowedLanguages
        canSpecifyLanguage
        serviceTranslationAvailable
      }
      stepqs {
        ...StepQParts
      }
    }
  }
  ${COMPANY_PARTS}
  ${STEPQ_PARTS}
`;

export const APPLICATION_COMPONENT_PARTS = gql`
  fragment AppComponentParts on ApplicationComponentType {
    id
    name
    adminName
    associatedData
    type
    instructionImage
    instructionHtml
    canSpecifyLanguage
  }
`;

export const UPLOADED_DOCUMENT_PARTS = gql`
  fragment UploadedDocumentParts on UploadedDocumentType {
    id
    created
    modified
    file
    fileName
    fileSize
    language
    needsTranslation
    translationOrder {
      orderId
      orderNumber
      status
      statusDisplay
    }
    translatedFile
    translatedFileName
    translatedFileUsePlaceholder
    ownedBy
  }
`;

export const INDEXED_FIELD_PARTS = gql`
  fragment IndexedFieldParts on IndexedFieldType {
    id
    created
    modified
    fieldName
    fieldVar
    safe
    priority
    preview
  }
`;

export const REL_TYPE_PARTS = gql`
  fragment RelationshipTypeParts on RelationshipTypeType {
    id
    type
  }
`;

export const XMLQ_PARTS = gql`
  fragment XmlQuestionnaireParts on XmlQuestionnaireType {
    id
    name
    relationshipType {
      ...RelationshipTypeParts
    }
  }
  ${REL_TYPE_PARTS}
`;

export const TAB_Q_PARTS = gql`
  fragment TabQParts on TabQuestionnaireType {
    id
    editPrompt
    startPrompt
    guestEnabled
    guestOnly
    required
    lockable
    questionnaires {
      ...XmlQuestionnaireParts
    }
  }
  ${XMLQ_PARTS}
`;

/** @deprecated use guest access token instead */
export const Q_ACCESS_TOKEN_PARTS = gql`
  fragment QuestionnaireAccessTokenParts on QuestionnaireAccessTokenType {
    id
    sendTo
    expirationDate
  }
`;

export const QUESTIONNAIRE_RESPONSE_PARTS = gql`
  fragment QuestionnaireResponseParts on QuestionnaireResponseType {
    id
    created
    completedAt
    locked
    questionnaireaccesstokenSet {
      ...QuestionnaireAccessTokenParts
    }
  }
  ${Q_ACCESS_TOKEN_PARTS}
`;

export const ROLE_PARTS = gql`
  fragment RoleParts on ManagerRoleType {
    id
    name
    allowedPermissions
  }
`;

export const MANAGER_ROLE_PARTS = gql`
  fragment ManagerRoleParts on ManagerRoleType {
    id
    name
    allowedPermissions
  }
`;

export const MANAGER_MEMBERSHIP_PARTS = gql`
  fragment ManagerMembershipParts on ManagerMembershipType {
    id
    user {
      ...UserParts
    }
    roles {
      ...ManagerRoleParts
    }
    accessStartDate
    accessEndDate
    reason
  }
  ${USER_PARTS}
  ${MANAGER_ROLE_PARTS}
`;

export const SSO_GROUP_ASSOCIATION_PARTS = gql`
  fragment SSOGroupAssociationParts on SSOGroupAssociationType {
    idp {
      providerName
    }
    groupsToMatch
    rolesToAssoc {
      ...RoleParts
    }
  }
  ${ROLE_PARTS}
`;

export const RECEIPT_PARTS = gql`
  fragment ReceiptParts on SCReceiptType {
    platformAmount
    legalServicesAmount
    totalAmount
    created
    status
    promoCode {
      code
    }
    forTier {
      name
      tierLevel
      applicationType {
        name
      }
    }
    forService {
      name
      displayName
    }
  }
`;

export const MANAGERS_QUERY = gql`
  query managersQuery {
    attorneyManagers {
      ...ManagerMembershipParts
    }
    paralegalManagers {
      ...ManagerMembershipParts
    }
  }
  ${MANAGER_MEMBERSHIP_PARTS}
`;

export const MY_APPLICANT_CASES_QUERY = gql`
  query MyApplicationCases($experienceType: String!) {
    me {
      currentApplicationcase {
        id
      }
      appCases {
        id
        created
        hasPaid
        isRefundedCase
        lastActivity
        archivedReason
        caseStatus
        outcomeStatus
        useFnMarkerProgress
        anchorCaseId
        completedStep
        applyingInUsa
        previewSections {
          name
          sectionType
          renderedHtml
        }
        application {
          id
          useFnMarkerProgress
          dasherizedName
          name
          applicationType {
            id
            name
          }
        }
        milestones {
          id
          name
          finishedDate
          timelineFinishedDate
          startedDate
          description
          descriptionOverride(experienceType: $experienceType)
        }
      }
    }
  }
`;

export const MY_CASE_COUNT_QUERY = gql`
  query MyCaseCount {
    me {
      appCases {
        id
      }
    }
  }
`;

export const MY_PURCHASED_SERVICES_QUERY = gql`
  query MyPurchasedServices {
    me {
      currentApplicationcase {
        id
      }
      receipts {
        status
        forTier {
          tierLevel
        }
        forService {
          name
        }
        appCase {
          id
        }
      }
    }
  }
`;

export const CASE_MILESTONES = gql`
  query CaseMilestones($id: UUID!, $experienceType: String!) {
    case(id: $id) {
      milestones {
        id
        name
        finishedDate
        timelineFinishedDate
        startedDate
        description
        descriptionOverride(experienceType: $experienceType)
      }
    }
  }
`;

export const CASE_COMPONENT_PARTS = gql`
  fragment CaseComponentParts on CaseComponentType {
    name
    displayName
    applicantDetails
    status
    sets
    shortName
    id
    componentId
    uniqueTogetherId
    required
    status
    optedOut
    actionButton
    manageButton
  }
`;

export const QUESTIONNAIRE_STEP_PARTS = gql`
  fragment QuestionnaireStepParts on QuestionnaireStepType {
    tabQuestionnaire {
      ...TabQParts
    }
    responses {
      ...QuestionnaireResponseParts
      user {
        firstName
        lastName
      }
    }
    hasResponse
    status
    actionButton
    manageButton
  }
  ${TAB_Q_PARTS}
  ${QUESTIONNAIRE_RESPONSE_PARTS}
`;

export const GENERATED_PDF_PARTS = gql`
  fragment GeneratedPDFParts on GeneratedPDFType {
    id
    pdf
    attachmentPdf
    userStatus
    adminStatus
    fileSize
  }
`;

export const INTEGRATION_UPLOAD_PARTS = gql`
  fragment IntegrationUploadParts on IntegrationUploadType {
    status
  }
`;

export const JOB_PARTS = gql`
  fragment JobParts on JobType {
    id
    jobTitle
  }
`;

export const JOB_POSTING_PARTS = gql`
  fragment JobPostingParts on JobPostingType {
    id
    publishedUrl
    integrationType
    status
    applicantsEmail
    datePublished
  }
`;

export const MANUAL_INTEGRATION_PARTS = gql`
  fragment ManualIntegrationParts on ManualIntegrationType {
    id
    name
    instructionHtml
    logo
    integrationuploadSet {
      ...IntegrationUploadParts
    }
  }
  ${INTEGRATION_UPLOAD_PARTS}
`;

export const SERVICE_UNIT_PARTS = gql`
  fragment ServiceUnitParts on ServiceUnitType {
    id
    name
    description
  }
`;

export const TEAM_PARTS = gql`
  fragment TeamParts on TeamType {
    id
    name
    created
    isDefaultTeam
    permissionGroupId
    companyMemberships {
      company {
        id
        name
      }
    }
    isRestrictedToMilestones
    restrictingMilestones
    isRestrictedToRegion
    isRestrictedToApplicationTypes
    restrictingApplicationTypes
    restrictingRegion {
      name
      id
    }
    managerMemberships {
      id
      accessStartDate
      accessEndDate
      permanent
      reason
      role {
        name
        id
      }
      user {
        id
        firstName
        lastName
        email
      }
      ssoGroupAssociation {
        id
      }
    }
    temporaryManagers {
      id
      accessStartDate
      accessEndDate
      permanent
      reason
      role {
        name
        id
      }
      user {
        id
        firstName
        lastName
        email
      }
      ssoGroupAssociation {
        id
      }
    }
    allowedPermissions
  }
`;

export const MY_APPLICANT_DOCUMENTS_QUERY = gql`
  query myDocuments {
    me {
      applicant {
        id
        documentWalletSet {
          ...UploadedDocumentParts
          component {
            ...AppComponentParts
          }
        }
      }
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
  ${APPLICATION_COMPONENT_PARTS}
`;

// We can extend this to take in company or case id instead of applicant
export const LATEST_UPLOADED_DOCUMENT_QUERY = gql`
  query lastestDoc(
    $componentId: ID!
    $applicantId: ID
    $depApplicantId: Int
    $recruitingResponseId: UUID
    $guestToken: String
    $specificTo: String
    $applicationCaseId: UUID
  ) {
    latestUploadedDocument(
      componentId: $componentId
      applicantId: $applicantId
      depApplicantId: $depApplicantId
      recruitingResponseId: $recruitingResponseId
      guestToken: $guestToken
      specificTo: $specificTo
      applicationCaseId: $applicationCaseId
    ) {
      ...UploadedDocumentParts
      associatedData
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
`;

export const ACCOUNT_SETTINGS_QUERY = gql`
  query accountSettings {
    me {
      ...UserParts
      receipts {
        ...ReceiptParts
      }

      questionnaireSteps {
        ...QuestionnaireStepParts
      }
    }
  }
  ${USER_PARTS}
  ${RECEIPT_PARTS}
  ${QUESTIONNAIRE_STEP_PARTS}
`;

export const MY_ACCOUNT_QUERY = gql`
  query myAccount {
    me {
      userComponentCategories {
        name
        userCaseIndexes {
          columns
          rows {
            applicationCase {
              created
            }
            component {
              application {
                name
              }
              component {
                name
                file
              }
            }
            uploadedDocuments {
              file
            }
          }
        }
      }
      receipts {
        ...ReceiptParts
      }
    }
  }
  ${RECEIPT_PARTS}
`;

export const STEPQ_QUERY = gql`
  query StepQ($tabqId: ID!, $caseId: UUID) {
    stepq(tabqId: $tabqId, caseId: $caseId) {
      ...StepQParts
    }
  }
  ${STEPQ_PARTS}
`;

export const STEPQ_SHORT_QUERY = gql`
  query StepQ($tabqId: ID!, $caseId: UUID) {
    stepq(tabqId: $tabqId, caseId: $caseId) {
      tabOrder
      prompt
    }
  }
`;

export const MANUAL_INTEGRATIONS_QUERY = gql`
  query ManualIntegrations {
    manualIntegrations {
      ...ManualIntegrationParts
    }
  }
  ${MANUAL_INTEGRATION_PARTS}
`;

export const INDEXED_FIELDS_QUERY = gql`
  query IndexedFields($caseId: UUID) {
    indexedFields(caseId: $caseId) {
      ...IndexedFieldParts
      value
    }
  }
  ${INDEXED_FIELD_PARTS}
`;

export const MY_CASES_QUERY = gql`
  query myCasesQuery(
    $viewType: CasesViewType
    $caseTypeFilter: String
    $statusTypeFilter: String
    $markerTypeFilter: String
    $page: Int
    $searchFilter: String
    $onlyAssigned: Boolean
    $includeArchived: Boolean
    $isInProgress: Boolean
    $isClosed: Boolean
    $isCompleted: Boolean
    $isOnHold: Boolean
    $sortColumn: SortByType
    $sortDirection: SortDirType
    $pageSize: Int
  ) {
    myCases(
      viewType: $viewType
      caseTypeFilter: $caseTypeFilter
      statusTypeFilter: $statusTypeFilter
      markerTypeFilter: $markerTypeFilter
      page: $page
      searchFilter: $searchFilter
      onlyAssigned: $onlyAssigned
      includeArchived: $includeArchived
      isInProgress: $isInProgress
      isClosed: $isClosed
      isCompleted: $isCompleted
      isOnHold: $isOnHold
      sortType: $sortColumn
      sortDir: $sortDirection
      pageSize: $pageSize
    ) {
      page
      numPages
      total
      hasNext
      hasPrev
      casesMeta {
        name
        value
        drilldownArg
        drilldownVal
      }
      cases {
        ...CaseParts
        fileNumber
        activeMarker {
          id
          name
        }
        applicant {
          ...ApplicantParts
          company {
            id
            created
            modified
            name
            logo
            companyLogo
            permissionGroupId
            applicantCount
            applicationcaseCount
            recruiterPassword
          }
        }
        application {
          id
          name
          applicationType {
            id
            name
          }
        }
        caseRoleAssignments {
          id
          assigneeType {
            id
            name
          }
          user {
            id
            email
            firstName
            lastName
          }
        }
      }
    }
  }
  ${CASE_PARTS}
  ${APPLICANT_PARTS}
`;

export const MY_APPLICANTS_QUERY = gql`
  query myApplicantsQuery(
    $viewType: ApplicantsViewType
    $companyTypeFilter: String
    $attorneyTypeFilter: String
    $continentTypeFilter: String
    $citizenshipTypeFilter: String
    $joblocTypeFilter: String
    $departmentTypeFilter: String
    $titleTypeFilter: String
    $page: Int
    $searchFilter: String
    $pageSize: Int
  ) {
    myApplicants(
      viewType: $viewType
      companyTypeFilter: $companyTypeFilter
      attorneyTypeFilter: $attorneyTypeFilter
      continentTypeFilter: $continentTypeFilter
      citizenshipTypeFilter: $citizenshipTypeFilter
      joblocTypeFilter: $joblocTypeFilter
      departmentTypeFilter: $departmentTypeFilter
      titleTypeFilter: $titleTypeFilter
      page: $page
      searchFilter: $searchFilter
      pageSize: $pageSize
    ) {
      page
      numPages
      total
      hasNext
      hasPrev
      currentLayer
      applicantsMeta {
        name
        value
        drilldownArg
        drilldownVal
      }
      applicants {
        ...ApplicantParts
        emailuser {
          email
        }
        birthdate
        jobTitle
        jobLocation
        immigrationStatus
        citizenship
        applicantCode
        company {
          id
          name
        }
      }
    }
  }
  ${APPLICANT_PARTS}
`;

export const CASE_ROLE_ASSIGNMENTS_QUERY = gql`
  query caseRoleAssignments($caseId: UUID!) {
    case(id: $caseId) {
      ...CaseParts
      outcomeStatus
      caseRoleAssignments {
        id
        user {
          id
          email
          firstName
          lastName
        }
        assigneeType {
          id
          name
        }
      }
    }
  }
  ${CASE_PARTS}
`;

export const CASE_DETAIL_QUERY = gql`
  query Case($caseId: UUID!) {
    case(id: $caseId) {
      ...CaseParts
      paymentTier
      hasResetJointSponsor
      jointSponsorAccess {
        pin
        link
        expirationDate
        jsEmail
        customerEmail
      }
      anchorCaseId
      previewFields {
        sensitiveField
        fieldName
        fieldValue
        fieldVar
        fieldType
        dataType
        section
      }
      previewSections {
        id
        name
        renderedHtml
        previewName
        sectionType
      }
      allowedPermissions
      outcomeStatus
      applicant {
        ...ApplicantParts
        birthdate
        applicantCode
        emailuserSet {
          id
          email
          connectPersonId
        }
        company {
          id
          name
        }
      }
      application {
        id
        name
        dasherizedName
        componentSets
        hasJobs
        hasUscisStatus
        applicationType {
          id
          name
        }
      }
      casemarkerSet {
        ...CaseMarkerParts
        assignee {
          id
          email
          firstName
          lastName
        }
      }
      snapshots {
        id
        name
        generatedPdf {
          id
          attachmentPdf
        }
      }
      myRoles
      formattedMailingAddress
    }
  }
  ${CASE_PARTS}
  ${APPLICANT_PARTS}
  ${CASE_MARKER_PARTS}
`;

export const CASE_DETAIL_POLLING_QUERY = gql`
  query caseDetailPolling($caseId: UUID!) {
    case(id: $caseId) {
      ...CaseParts
      allowedPermissions
      applicant {
        ...ApplicantParts
        emailuserSet {
          id
          email
        }
        company {
          id
          name
        }
      }
      application {
        id
        name
        componentSets
        hasJobs
        hasUscisStatus
        applicationType {
          id
          name
        }
      }
      casemarkerSet {
        ...CaseMarkerParts
        assignee {
          email
          firstName
          lastName
        }
      }
      myRoles
    }
  }
  ${CASE_PARTS}
  ${APPLICANT_PARTS}
  ${CASE_MARKER_PARTS}
`;

export const SUPPORTING_DOCUMENTS_TAB_QUERY = gql`
  query supportingDocumentsTabQuery($caseId: UUID!, $caseUUID: UUID!, $stepsFilter: ComponentFilterEnum) {
    case(id: $caseId) {
      id
      caseComponents {
        id
        componentId
        translationNotes
        uploadedDocuments {
          ...UploadedDocumentParts
          displayOrder
          applicant {
            id
          }
          component {
            id
            name
            canSpecifyLanguage
          }
        }
      }
    }
    infoSteps(caseId: $caseUUID, stepsFilter: $stepsFilter) {
      name
      infoType
      step {
        ... on CaseComponentType {
          id
          componentId
          required
          actionButton
          specificTo
          useRelation
          component {
            id
            name
            canSpecifyLanguage
          }
        }
      }
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
`;

export const MY_TASKS_QUERY = gql`
  query Case($id: UUID!) {
    case(id: $id) {
      id
      applicant {
        id
      }
      caseStatus
      isEditableByCustomer
      isReadonlyForCaseworker
      casemarkerSet(currentUserOnly: true) {
        ...CaseMarkerParts
        assignee {
          email
        }
      }
    }
  }
  ${CASE_MARKER_PARTS}
`;

export const CASE_PROGRESS_QUERY = gql`
  query Case($caseId: UUID!) {
    case(id: $caseId) {
      ...CaseParts
      allowedPermissions
      casemarkerSet {
        ...CaseMarkerParts
        case {
          id
          applicant {
            id
          }
        }
        assignee {
          email
        }
      }
      applicant {
        ...ApplicantParts
        emailuserSet {
          id
          email
        }
        company {
          id
          name
        }
      }
    }
  }
  ${CASE_PARTS}
  ${APPLICANT_PARTS}
  ${CASE_MARKER_PARTS}
`;

export const AVAILABLE_MARKER_ASSIGNMENTS_QUERY = gql`
  query availableMarkerAssignments($caseId: UUID!, $markerId: UUID!) {
    case(id: $caseId) {
      id
      caseMarker(id: $markerId) {
        id
        availableAssignments {
          ...UserParts
        }
      }
    }
  }
  ${USER_PARTS}
`;

export const CHANGE_MARKER_ASSIGNEE = gql`
  mutation changeMarkerAssignee($email: String!, $markerId: UUID!, $sendEmail: Boolean) {
    changeMarkerAssignee(email: $email, markerId: $markerId, sendEmail: $sendEmail) {
      caseMarker {
        id
        name
        assignee {
          email
        }
      }
    }
  }
`;

export const CASE_NOTES_QUERY = gql`
  query CaseNotes($caseId: UUID!) {
    case(id: $caseId) {
      id
      noteSet {
        id
        noteType
        noteDisplayType
        text
        created
        userBy {
          email
        }
      }
      casenotificationpreferencesSet {
        id
      }
    }
  }
`;

export const CASE_QSTEP_QUERY = gql`
  query caseQuestionnaireStep($caseId: UUID, $tabqId: ID) {
    case(id: $caseId) {
      id
      questionnaireStep(tabqId: $tabqId) {
        ...QuestionnaireStepParts
      }
    }
  }
  ${QUESTIONNAIRE_STEP_PARTS}
`;

export const CASE_DETAIL_JOBS_QUERY = gql`
  query caseWithJobPostings($caseId: UUID!) {
    case(id: $caseId) {
      id
      applicant {
        id
        company {
          id
        }
      }
      job {
        ...JobParts
        jobPostings {
          ...JobPostingParts
        }
      }
    }
  }
  ${JOB_PARTS}
  ${JOB_POSTING_PARTS}
`;

export const CASE_DETAIL_JOB_APPLICANTS = gql`
  query caseJobApplicants($caseId: UUID!) {
    case(id: $caseId) {
      id
      job {
        ...JobParts
        jobPostings {
          ...JobPostingParts
          jobApplicants {
            id
            created
            firstName
            lastName
            rejectionReason
            resume
          }
        }
      }
    }
  }
  ${JOB_PARTS}
  ${JOB_POSTING_PARTS}
`;

export const CASE_FEEDBACK = gql`
  query caseFeedback($caseId: UUID!) {
    case(id: $caseId) {
      id
      reviews {
        id
        reviewText
        reviewType
        finalized
      }
    }
  }
`;

export const EDIT_JOB_APPLICANT_MUTATION = gql`
  mutation editJobApplicant($jobApplicantId: ID, $rejectionReason: String) {
    editJobApplicant(jobApplicantId: $jobApplicantId, rejectionReason: $rejectionReason) {
      jobApplicant {
        id
        created
        firstName
        lastName
        rejectionReason
        resume
      }
    }
  }
`;

export const NAICS_CODES_QUERY = gql`
  query naicsQuery($searchTerm: String) {
    naicsCodes(searchTerm: $searchTerm) {
      id
      naicsId
      description
    }
  }
`;

export const POLL_TASK_STATUS_QUERY = gql`
  query pollTasksStatusQuery($id: UUID) {
    automationTask(id: $id) {
      status
      automationType
      errorMessage
    }
  }
`;

export const CASE_COMPONENT_QUERY = gql`
  query CaseComponent($componentId: ID!, $caseId: UUID!) {
    caseComponent(componentId: $componentId, caseId: $caseId) {
      ...CaseComponentParts
      component {
        id
        name
        adminName
        associatedData
        type
        instructionImage
        instructionHtml
      }
      uploadedDocument {
        id
        created
        modified
        file
        fileName
        language
        needsTranslation
        translatedFile
        translatedFileName
        translatedFileUsePlaceholder
        ownedBy
      }
      uploadedDocuments {
        id
        created
        modified
        file
        fileName
        language
        needsTranslation
        translatedFile
        translatedFileName
        translatedFileUsePlaceholder
        ownedBy
      }
    }
  }
  ${CASE_COMPONENT_PARTS}
`;

export const CREATE_APPLICANT_MUTATION = gql`
  mutation createApplicant(
    $firstName: String!
    $lastName: String!
    $companyId: ID!
    $locationId: ID
    $email: String!
    $sendInvite: Boolean
    $ssqId: ID
    $data: GenericObjectField
  ) {
    createApplicant(
      firstName: $firstName
      lastName: $lastName
      companyId: $companyId
      locationId: $locationId
      email: $email
      sendInvite: $sendInvite
      ssqId: $ssqId
      data: $data
    ) {
      applicant {
        ...ApplicantParts
        emailuserSet {
          ...UserParts
        }
      }
    }
  }
  ${APPLICANT_PARTS}
  ${USER_PARTS}
`;

export const CREATE_NOTE_MUTATION = gql`
  mutation createNote($caseId: UUID, $applicantId: ID, $text: String!) {
    createNote(noteData: { caseId: $caseId, applicantId: $applicantId, text: $text }) {
      note {
        ...NoteParts
      }
    }
  }
  ${NOTE_PARTS}
`;

export const SEND_USER_INVITE_MUTATION = gql`
  mutation sendUserInvite($caseId: UUID!) {
    sendUserInvite(sendUserInviteData: { caseId: $caseId }) {
      case {
        ...CaseParts
      }
    }
  }
  ${CASE_PARTS}
`;

export const SEND_DOC_REQUEST_MUTATION = gql`
  mutation sendDocRequest($caseId: UUID!, $componentId: ID!) {
    sendDocRequest(sendDocRequestData: { caseId: $caseId, componentId: $componentId }) {
      sendRequest
    }
  }
`;

export const MANUAL_START_MARKER_MUTATION = gql`
  mutation startMarker($markerId: UUID!, $startedDate: DateTime!) {
    updateMarker(markerData: { id: $markerId, startedDate: $startedDate }) {
      marker {
        ...CaseMarkerParts
        case {
          id
          applicant {
            id
          }
        }
      }
    }
  }
  ${CASE_MARKER_PARTS}
`;

export const MANUAL_FINISH_MARKER_MUTATION = gql`
  mutation finishMarker($markerId: UUID!, $finishedDate: DateTime!) {
    updateMarker(markerData: { id: $markerId, finishedDate: $finishedDate }) {
      marker {
        ...CaseMarkerParts
        case {
          id
          applicant {
            id
          }
        }
      }
    }
  }
  ${CASE_MARKER_PARTS}
`;

export const CREATE_QR_MUTATION = gql`
  mutation createQResponse(
    $applicantId: ID
    $caseId: UUID
    $relTypeId: ID
    $relFirstname: String
    $relLastname: String
    $companyId: ID
    $businessEntityId: Int
    $userId: UUID
    $xmlqId: ID!
  ) {
    createQResponse(
      data: {
        applicantId: $applicantId
        caseId: $caseId
        relTypeId: $relTypeId
        relFirstname: $relFirstname
        relLastname: $relLastname
        companyId: $companyId
        businessEntityId: $businessEntityId
        userId: $userId
        xmlqId: $xmlqId
      }
    ) {
      qResponse {
        id
      }
    }
  }
`;

export const UPLOADED_DOCUMENT_QUERY = gql`
  query uploadedDocument($uploadedDocumentId: ID!) {
    uploadedDocument(uploadedDocumentId: $uploadedDocumentId) {
      ...UploadedDocumentParts
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
`;

export const CREATE_UPLOADED_DOCUMENT_MUTATION = gql`
  mutation createUploadedDocument(
    $file: Upload
    $component: ID!
    $applicant: ID
    $applicationCase: UUID
    $company: ID
    $recruitingResponse: UUID
    $language: String
    $isPrivate: Boolean
    $requiresTranslation: Boolean
    $validateDocumentNameForApplicant: Boolean
    $extractOcrData: Boolean
  ) {
    createUploadedDocument(
      file: $file
      component: $component
      applicant: $applicant
      applicationCase: $applicationCase
      company: $company
      recruitingResponse: $recruitingResponse
      language: $language
      isPrivate: $isPrivate
      requiresTranslation: $requiresTranslation
      validateDocumentNameForApplicant: $validateDocumentNameForApplicant
      extractOcrData: $extractOcrData
    ) {
      document {
        ...UploadedDocumentParts
      }
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
`;

export const UPDATE_UPLOADED_DOCUMENT_MUTATION = gql`
  mutation updateUploadedDocument(
    $id: ID!
    $file: Upload
    $language: String
    $isPrivate: Boolean
    $requiresTranslation: Boolean
    $validateDocumentNameForApplicant: Boolean
    $extractOcrData: Boolean
    $isTranslated: Boolean
    $translatedFileUsePlaceholder: Boolean
    $applicationCase: UUID
  ) {
    updateUploadedDocument(
      id: $id
      file: $file
      language: $language
      isPrivate: $isPrivate
      requiresTranslation: $requiresTranslation
      validateDocumentNameForApplicant: $validateDocumentNameForApplicant
      extractOcrData: $extractOcrData
      isTranslated: $isTranslated
      translatedFileUsePlaceholder: $translatedFileUsePlaceholder
      applicationCase: $applicationCase
    ) {
      document {
        ...UploadedDocumentParts
      }
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
`;

export const DELETE_UPLOADED_DOCUMENT_MUTATION = gql`
  mutation deleteUploadedDocument($id: ID!) {
    deleteUploadedDocument(id: $id) {
      result
    }
  }
`;

export const DELETE_UPLOADED_TRANSLATED_DOCUMENT_MUTATION = gql`
  mutation deleteUploadedTranslatedDocument($id: ID!) {
    deleteUploadedTranslatedDocument(id: $id) {
      result
    }
  }
`;

export const REORDER_UPLOADED_DOCUMENTS_MUTATION = gql`
  mutation reorderUploadedDocuments($documentOrders: [ReorderDocumentInput]!) {
    reorderUploadedDocuments(documentOrders: $documentOrders) {
      documents {
        id
        displayOrder
      }
    }
  }
`;

export const TRANSLATE_UPLOADED_DOCUMENT_MUTATION = gql`
  mutation translateUploadedDocument(
    $uploadedDocumentId: ID!
    $applicationCaseId: UUID!
    $sourceLanguage: String!
    $targetLanguage: String!
    $notarization: Boolean!
    $expedited: Boolean!
    $notes: String
  ) {
    createTranslationOrder(
      uploadedDocumentId: $uploadedDocumentId
      applicationCaseId: $applicationCaseId
      sourceLanguage: $sourceLanguage
      targetLanguage: $targetLanguage
      notarization: $notarization
      expedited: $expedited
      notes: $notes
    ) {
      orderId
    }
  }
`;

export const CREATE_TRANSLATION_REVISION = gql`
  mutation createTranslationRevision($orderId: String!, $revisionText: String!, $revisionFile: Upload, $applicant: ID) {
    createTranslationRevision(
      orderId: $orderId
      revisionText: $revisionText
      revisionFile: $revisionFile
      applicant: $applicant
    ) {
      revisionId
    }
  }
`;

export const SET_CURRENT_CASE = gql`
  mutation setCurrentCase($caseId: UUID!) {
    setCurrentCase(caseId: $caseId) {
      success
      message
    }
  }
`;

export const CANCEL_TRANSLATION_ORDER_MUTATION = gql`
  mutation cancelTranslationOrder($translationOrderId: String!, $applicationCaseId: UUID!) {
    cancelTranslationOrder(orderId: $translationOrderId, applicationCaseId: $applicationCaseId) {
      status
    }
  }
`;

export const CREATE_RELATION_MUTATION = gql`
  mutation createRelation($applicantId: ID, $firstName: String, $lastName: String, $typeId: ID) {
    createRelation(applicantId: $applicantId, firstName: $firstName, lastName: $lastName, typeId: $typeId) {
      applicantRelationship {
        id
        firstName
        lastName
        applicant {
          id
        }
        type {
          id
        }
      }
    }
  }
`;

export const CREATE_RESPONSE_MUTATION = gql`
  mutation createResponse($applicantId: ID, $caseId: UUID, $relationshipId: ID, $xmlqId: ID) {
    createResponse(applicantId: $applicantId, caseId: $caseId, relationshipId: $relationshipId, xmlqId: $xmlqId) {
      questionnaireResponse {
        id
        applicant {
          id
        }
        responseType
      }
    }
  }
`;

export const APPLY_ATTORNEY_REVIEW_TAGS = gql`
  mutation applyAttorneyReviewTags($caseId: UUID!, $attorneyReviewTags: JSONString!) {
    applyAttorneyReviewTags(caseId: $caseId, attorneyReviewTags: $attorneyReviewTags) {
      case {
        ...CaseParts
      }
    }
  }
  ${CASE_PARTS}
`;

export const ACTIVE_LLM_MODEL_CONFIGS = gql`
  query ActiveLlmModelConfigs {
    activeLlmModelConfigs {
      slugId
      name
    }
  }
`;

export const ACTIVE_PROMPTS = gql`
  query ActivePrompts {
    activePrompts {
      slugId
      name
    }
  }
`;

export const MAKE_LLM_CALL = gql`
  mutation makeLlmCall($promptSlug: String!, $modelSlug: String!, $text: String!, $caseId: UUID!) {
    makeLlmCall(promptSlug: $promptSlug, modelSlug: $modelSlug, text: $text, caseId: $caseId) {
      result
    }
  }
`;

export const UPDATE_CASE_DATA_PATCH = gql`
  mutation updateCaseData($caseId: UUID!, $patchData: JSONString!) {
    updateCaseData(id: $caseId, patchData: $patchData) {
      case {
        id
      }
    }
  }
`;

export const UPDATE_CASE_ROLE_MUTATION = gql`
  mutation updateCaseRole($caseId: UUID!, $assignees: [CaseAssignmentsInput]!) {
    updateCaseRoleAssignment(caseId: $caseId, assignees: $assignees) {
      case {
        ...CaseParts
      }
    }
  }
  ${CASE_PARTS}
`;

export const UPDATE_CASE_STATUS_MUTATION = gql`
  mutation updateCaseStatus(
    $caseId: UUID!
    $status: CaseStatus!
    $outcomeStatus: OutcomeStatus
    $reason: String
    $deadlineDate: DateTime
  ) {
    updateCaseStatus(
      caseId: $caseId
      newCaseStatus: $status
      newOutcomeStatus: $outcomeStatus
      reason: $reason
      deadlineDate: $deadlineDate
    ) {
      case {
        ...CaseParts
        outcomeStatus
      }
    }
  }
  ${CASE_PARTS}
`;

export const UPDATE_APPLICANT_MUTATION = gql`
  mutation updateApplicant($applicantId: ID!, $firstName: String, $lastName: String) {
    updateApplicant(applicantData: { id: $applicantId, firstName: $firstName, lastName: $lastName }) {
      applicant {
        ...ApplicantParts
      }
    }
  }
  ${APPLICANT_PARTS}
`;

export const DELETE_QUESTIONNAIRE_RESPONSE_MUTATION = gql`
  mutation deleteQResponse($qrId: UUID!) {
    deleteQResponse(qrId: $qrId) {
      success
    }
  }
`;

export const RESET_JOINT_SPONSOR_DATA_MUTATION = gql`
  mutation resetJointSponsorData(
    $applicantId: ID!
    $caseId: UUID!
    $spoFirstName: String
    $spoMiddleName: String
    $spoLastName: String
    $spoEmailAddress: String
  ) {
    resetJointSponsorData(
      applicantId: $applicantId
      caseId: $caseId
      spoFirstName: $spoFirstName
      spoMiddleName: $spoMiddleName
      spoLastName: $spoLastName
      spoEmailAddress: $spoEmailAddress
    ) {
      success
      pin
      link
      expirationDate
      jsEmail
      customerEmail
    }
  }
`;

export const EXTEND_JOINT_SPONSOR_ACCESS_MUTATION = gql`
  mutation extendJointSponsorAccess($caseId: UUID!, $sendEmail: Boolean) {
    extendJointSponsorAccess(caseId: $caseId, sendEmail: $sendEmail) {
      success
      pin
      link
      expirationDate
      jsEmail
      customerEmail
    }
  }
`;

export const UPDATE_APPLICANT_ATTRIBUTE_MUTATION = gql`
  mutation updateApplicantAttribute($applicantId: ID!, $firstName: String, $lastName: String, $email: String) {
    updateApplicantAttribute(applicantId: $applicantId, firstName: $firstName, lastName: $lastName, email: $email) {
      applicant {
        id
        firstName
        lastName
        emailuser {
          ...UserParts
        }
      }
    }
  }
  ${USER_PARTS}
`;

export const INFO_STEPS_QUERY = gql`
  query infoSteps($caseId: UUID, $stepsFilter: ComponentFilterEnum) {
    infoSteps(caseId: $caseId, stepsFilter: $stepsFilter) {
      name
      infoType
      step {
        ... on QuestionnaireStepType {
          ...QuestionnaireStepParts
          primaryCaseId
          primaryApplicantId
          primaryApplicantName
          displayName
        }
        ... on CaseComponentType {
          ...CaseComponentParts
          primaryCaseId
          primaryApplicantId
          primaryApplicantName
          component {
            id
            name
            adminName
            associatedData
            type
            instructionImage
            instructionHtml
          }
          uploadedDocument {
            id
            created
            modified
            file
            fileName
            language
            needsTranslation
            translatedFile
            translatedFileName
            translatedFileUsePlaceholder
            ownedBy
          }
          uploadedDocuments {
            id
            created
            modified
            file
            fileName
            language
            needsTranslation
            translatedFile
            translatedFileName
            translatedFileUsePlaceholder
            ownedBy
          }
        }
      }
    }
  }
  ${QUESTIONNAIRE_STEP_PARTS}
  ${CASE_COMPONENT_PARTS}
`;

export const CASE_COMPONENTS_QUERY = gql`
  query caseComponents($caseId: UUID!) {
    caseComponents(caseId: $caseId) {
      ...CaseComponentParts
      component {
        id
        name
        adminName
        associatedData
        type
        instructionImage
        instructionHtml
      }
      uploadedDocument {
        id
        created
        modified
        file
        fileName
        language
        needsTranslation
        translatedFile
        translatedFileName
        translatedFileUsePlaceholder
        ownedBy
      }
      uploadedDocuments {
        id
        created
        modified
        file
        fileName
        fileSize
        language
        needsTranslation
        translatedFile
        translatedFileName
        translatedFileUsePlaceholder
        ownedBy
      }
    }
  }
  ${CASE_COMPONENT_PARTS}
`;

export const GENERATE_PDF_MUTATION = gql`
  mutation generatePdf(
    $applicationCaseId: UUID!
    $componentIds: [ID]
    $componentObjects: [GenericObjectField]
    $componentSet: String
    $watermark: Boolean
    $password: String
    $flatten: Boolean
    $repair: Boolean
    $vizRuleContext: String
    $childrenOptOut: Boolean
    $newGeneration: Boolean
    $translationReview: Boolean
  ) {
    generatePdf(
      generatePdfData: {
        applicationCaseId: $applicationCaseId
        componentIds: $componentIds
        componentObjects: $componentObjects
        componentSet: $componentSet
        watermark: $watermark
        password: $password
        flatten: $flatten
        vizRuleContext: $vizRuleContext
        childrenOptOut: $childrenOptOut
        translationReview: $translationReview
      }
      repair: $repair
      newGeneration: $newGeneration
    ) {
      generatedPdf {
        ...GeneratedPDFParts
        errorData
      }
    }
  }
  ${GENERATED_PDF_PARTS}
`;

export const GENERATED_PDF_QUERY = gql`
  query generatedPdf($id: UUID!) {
    generatedPdf(id: $id) {
      ...GeneratedPDFParts
      errorData
    }
  }
  ${GENERATED_PDF_PARTS}
`;

export const EMAIL_PDF_MUTATION = gql`
  mutation emailPdf($toEmail: String!, $pdfId: UUID!) {
    emailPdf(emailPdfData: { toEmail: $toEmail, pdfId: $pdfId }) {
      generatedPdf {
        ...GeneratedPDFParts
      }
    }
  }
  ${GENERATED_PDF_PARTS}
`;

export const SEND_QUESTIONNAIRE_INVITE_MUTATION = gql`
  mutation sendQuestionnaireInvite(
    $email: String!
    $caseId: UUID!
    $relType: String!
    $xmlqId: ID!
    $relFirstName: String
    $relLastName: String
  ) {
    sendQuestionnaireInvite(
      inviteData: {
        email: $email
        caseId: $caseId
        relType: $relType
        xmlqId: $xmlqId
        relFirstName: $relFirstName
        relLastName: $relLastName
      }
    ) {
      token {
        ...QuestionnaireAccessTokenParts
      }
      qResponse {
        ...QuestionnaireResponseParts
      }
    }
  }
  ${Q_ACCESS_TOKEN_PARTS}
  ${QUESTIONNAIRE_RESPONSE_PARTS}
`;

export const UPLOAD_INTEGRATION_MUTATION = gql`
  mutation uploadIntegration($file: Upload!, $manualIntegrationId: ID!) {
    uploadIntegration(file: $file, manualIntegrationId: $manualIntegrationId) {
      integrationUpload {
        ...IntegrationUploadParts
      }
    }
  }
  ${INTEGRATION_UPLOAD_PARTS}
`;

export const SET_CASE_NOTIFICATION_PREFERENCES = gql`
  mutation setCaseNotificationPreferences($caseId: UUID!, $subscribe: Boolean!) {
    setCaseNotificationPreferences(caseId: $caseId, subscribe: $subscribe) {
      case {
        id
        noteSet {
          ...NoteParts
        }
        casenotificationpreferencesSet {
          id
        }
      }
    }
  }
  ${NOTE_PARTS}
`;

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation changePassword($oldPassword: String!, $newPassword: String!) {
    changePassword(oldPassword: $oldPassword, newPassword: $newPassword) {
      user {
        ...UserParts
      }
    }
  }
  ${USER_PARTS}
`;

export const UPLOAD_USER_PHOTO_MUTATION = gql`
  mutation uploadUserPhoto($file: Upload!) {
    uploadUserPhoto(file: $file) {
      user {
        ...UserParts
      }
    }
  }
  ${USER_PARTS}
`;

export const JOB_TEMPLATES_QUERY = gql`
  query jobTemplates($companyId: ID!, $searchTerm: String) {
    jobTemplates(companyId: $companyId, searchTerm: $searchTerm) {
      ...JobTemplateParts
    }
  }
  ${JOB_TEMPLATE_PARTS}
`;

export const QR_COMPANY_LOCATIONS_QUERY = gql`
  query qrCompanyLocations($qrId: UUID!, $searchTerm: String) {
    questionnaireResponse(id: $qrId) {
      id
      availableLocations(searchTerm: $searchTerm) {
        ...CompanyLocationParts
      }
    }
  }
  ${COMPANY_LOCATION_PARTS}
`;

export const ARCHIVE_CASE_MUTATION = gql`
  mutation archiveCase($caseId: UUID!, $reason: String) {
    archiveCase(caseId: $caseId, reason: $reason) {
      case {
        ...CaseParts
      }
    }
  }
  ${CASE_PARTS}
`;

export const SAVE_CASE_FEEDBACK = gql`
  mutation saveCaseFeedback($caseId: UUID!, $reviewText: String!, $reviewType: String!, $finalized: Boolean!) {
    createOrUpdateCaseReview(caseId: $caseId, reviewText: $reviewText, reviewType: $reviewType, finalized: $finalized) {
      review {
        id
        applicationCase {
          id
        }
        finalized
        reviewType
        reviewText
      }
    }
  }
`;

export const CREATE_JOB_MUTATION = gql`
  mutation createJob(
    $companyId: ID!
    $caseId: UUID
    $jobTitle: String
    $jobCategory: String
    $jobSalary: Int
    $jobType: String
    $jobDescription: String
    $jobLocation: String
  ) {
    createJob(
      companyId: $companyId
      caseId: $caseId
      jobTitle: $jobTitle
      jobCategory: $jobCategory
      jobSalary: $jobSalary
      jobType: $jobType
      jobDescription: $jobDescription
      jobLocation: $jobLocation
    ) {
      job {
        ...JobParts
      }
    }
  }
  ${JOB_PARTS}
`;

export const USERS_SEARCH = gql`
  query userSearch($searchTerms: String) {
    users(search: $searchTerms) {
      ...UserParts
    }
  }
  ${USER_PARTS}
`;

export const BUSINESS_UNITS_QUERY = gql`
  query businessUnits {
    businessUnits {
      ...BusinessUnitParts
    }
  }
  ${BUSINESS_UNIT_PARTS}
`;

export const COMPANY_EMPLOYEES = gql`
  query companyEmployees($companyId: ID!, $search: String) {
    company(id: $companyId) {
      id
      applicantSearch(search: $search) {
        ...ApplicantParts
        realEmployer
        orgRelationship
        company {
          id
          name
        }
        emailuser {
          ...UserParts
          isUserPasswordSet
          hasB2bPasswordSetupInvite
        }
      }
    }
  }
  ${USER_PARTS}
  ${APPLICANT_PARTS}
`;

export const SEND_B2B_APPLICANT_PASSWORD_SETUP_INVITE_MUTATION = gql`
  mutation sendB2bApplicantPasswordSetupInvite($applicantId: ID!) {
    sendB2bApplicantPasswordSetupInvite(applicantId: $applicantId) {
      user {
        id
        isUserPasswordSet
        hasB2bPasswordSetupInvite
      }
    }
  }
`;

export const BUSINESS_UNITS = gql`
  query businessUnits($companyId: ID) {
    company(id: $companyId) {
      id
      name
      businessUnits {
        id
        name
        applicantCount
        caseCount
        allowedPermissions
        unitOwner {
          ...UserParts
        }
      }
    }
  }
  ${USER_PARTS}
`;

export const BUSINESS_UNIT_DETAIL = gql`
  query businessUnitDetail($companyId: ID, $businessUnitId: UUID!) {
    company(id: $companyId) {
      id
      name
    }
    businessUnit(unitId: $businessUnitId) {
      id
      name
      permissionGroupId
      allowedPermissions
      unitOwner {
        ...UserParts
      }
    }
  }
  ${USER_PARTS}
`;

export const BUSINESS_UNIT_DETAIL_EMPLOYEES = gql`
  query businessUnitDetailEmployees($businessUnitId: UUID!) {
    businessUnit(unitId: $businessUnitId) {
      id
      applicants {
        ...ApplicantParts
      }
    }
  }
  ${APPLICANT_PARTS}
`;

export const BUSINESS_UNIT_DETAIL_CASES = gql`
  query businessUnitDetailCases($businessUnitId: UUID!) {
    businessUnit(unitId: $businessUnitId) {
      id
      cases {
        ...CaseParts
        applicant {
          ...ApplicantParts
        }
        application {
          id
          applicationType {
            id
            name
          }
        }
      }
    }
  }
  ${APPLICANT_PARTS}
  ${CASE_PARTS}
`;

export const COMPANY_MANAGERS = gql`
  query companyManagers($companyId: ID) {
    company(id: $companyId) {
      id
      name
      permissionGroupId
      managerMemberships {
        id
        roles {
          ...RoleParts
        }
        user {
          ...UserParts
        }
        inheritedFrom
        ssoGroupAssociation {
          ...SSOGroupAssociationParts
        }
        accessStartDate
        accessEndDate
      }
    }
  }
  ${USER_PARTS}
  ${ROLE_PARTS}
  ${SSO_GROUP_ASSOCIATION_PARTS}
`;

export const BUSINESS_UNIT_MANAGERS = gql`
  query businessUnitManagers($unitId: UUID!) {
    businessUnit(unitId: $unitId) {
      id
      name
      permissionGroupId
      managerMemberships {
        id
        roles {
          ...RoleParts
        }
        user {
          ...UserParts
        }
        inheritedFrom
        accessStartDate
        accessEndDate
      }
    }
  }
  ${USER_PARTS}
  ${ROLE_PARTS}
`;

export const CREATE_MANAGER_MEMBERSHIP_MUTATION = gql`
  mutation createManagerMembership(
    $userId: UUID
    $permissionGroupId: UUID
    $roleIds: [Int]
    $accessStartDate: DateTime
    $accessEndDate: DateTime
    $reason: String
  ) {
    createManagerMembership(
      userId: $userId
      permissionGroupId: $permissionGroupId
      roleIds: $roleIds
      accessStartDate: $accessStartDate
      accessEndDate: $accessEndDate
      reason: $reason
    ) {
      membership {
        id
        accessEndDate
        accessStartDate
        reason
        roles {
          ...RoleParts
        }
        user {
          ...UserParts
        }
      }
      company {
        id
        name
        permissionGroupId
        managerMemberships {
          id
          roles {
            ...RoleParts
          }
          user {
            ...UserParts
          }
          inheritedFrom
        }
      }
      businessUnit {
        id
        name
        permissionGroupId
        managerMemberships {
          id
          roles {
            ...RoleParts
          }
          user {
            ...UserParts
          }
          inheritedFrom
        }
      }
    }
  }
  ${ROLE_PARTS}
  ${USER_PARTS}
`;

export const EDIT_MANAGER_MEMBERSHIP_MUTATION = gql`
  mutation editManagerMembership(
    $userId: UUID
    $membershipId: ID
    $roleIds: [Int]
    $accessStartDate: DateTime
    $accessEndDate: DateTime
    $reason: String
  ) {
    editManagerMembership(
      userId: $userId
      membershipId: $membershipId
      roleIds: $roleIds
      accessStartDate: $accessStartDate
      accessEndDate: $accessEndDate
      reason: $reason
    ) {
      membership {
        id
        accessEndDate
        accessStartDate
        reason
        roles {
          ...RoleParts
        }
        user {
          ...UserParts
        }
      }
      company {
        id
        name
        permissionGroupId
        managerMemberships {
          id
          roles {
            ...RoleParts
          }
          user {
            ...UserParts
          }
        }
      }
      businessUnit {
        id
        name
        permissionGroupId
        managerMemberships {
          id
          roles {
            ...RoleParts
          }
          user {
            ...UserParts
          }
        }
      }
    }
  }
  ${ROLE_PARTS}
  ${USER_PARTS}
`;

export const DELETE_MANAGER_MEMBERSHIP_MUTATION = gql`
  mutation deleteManagerMembership($membershipId: ID) {
    deleteManagerMembership(membershipId: $membershipId) {
      company {
        id
        name
        permissionGroupId
        managerMemberships {
          id
          roles {
            ...RoleParts
          }
          user {
            ...UserParts
          }
        }
      }
      businessUnit {
        id
        name
        permissionGroupId
        managerMemberships {
          id
          roles {
            ...RoleParts
          }
          user {
            ...UserParts
          }
        }
      }
    }
  }
  ${ROLE_PARTS}
  ${USER_PARTS}
`;

export const CREATE_BUSINESS_UNIT_MUTATION = gql`
  mutation createBusinessUnit($companyId: ID!, $name: String!, $ownerId: UUID) {
    createBusinessUnit(companyId: $companyId, name: $name, ownerId: $ownerId) {
      company {
        id
        businessUnit {
          id
          name
          applicantCount
          caseCount
          unitOwner {
            ...UserParts
          }
        }
      }
    }
  }
  ${USER_PARTS}
`;

export const COMPANY_APPLICANTS_SEARCH = gql`
  query companyApplicantsSearch($companyId: ID, $searchTerms: String) {
    company(id: $companyId) {
      id
      applicantSearch(search: $searchTerms) {
        ...ApplicantParts
      }
    }
  }
  ${APPLICANT_PARTS}
`;

export const COMPANY_CASES_SEARCH = gql`
  query companyCasesSearch($companyId: ID, $searchTerms: String) {
    company(id: $companyId) {
      id
      caseSearch(search: $searchTerms) {
        id
        application {
          id
          applicationType {
            id
            name
          }
        }
        applicant {
          ...ApplicantParts
        }
      }
    }
  }
  ${APPLICANT_PARTS}
`;

export const CREATE_USER_MUTATION = gql`
  mutation createUser($firstName: String, $lastName: String, $email: String) {
    createUser(firstName: $firstName, lastName: $lastName, email: $email) {
      user {
        ...UserParts
      }
    }
  }
  ${USER_PARTS}
`;

export const APPLICATION_TABS_QUERY = gql`
  query applicationTabsQuery($caseId: UUID!) {
    case(id: $caseId) {
      id
      applicationTabs {
        id
        title
        paymentRequired
        customType
        customDirective
        startText
        completedText
        created
        modified
        order
        failedEligibilityText
        video
      }
    }
  }
`;

export const APPLICATION_TAB_STEPQS_QUERY = gql`
  query applicationTabStepqsQuery($tabId: ID, $caseId: UUID) {
    stepqs(tabId: $tabId, caseId: $caseId) {
      tabId
      tabqId
      tabTitle
      tabOrder
      guestEnabled
      guestOnly
      many
      required
      startPrompt
      editPrompt
      prompt
      questionnaireText
      singleXml
      questionnaireNames
      questionnaireIds
      questionnaireReltypeIds
      questionnaireReltypeTypes
      hasResponse
      responses {
        responseId
        responseXmlId
        responseCompletedAt
        responseIrk
        responseRelFirstName
        responseRelLastName
        responseRelType
        responseRelId
        accessToken {
          id
          sendTo
          expirationDate
        }
      }
      responseId
      responseXmlId
      responseCompletedAt
      responseIrk
      responseRelFirstName
      responseRelLastName
      responseRelType
      responseRelId
      accessToken {
        id
        sendTo
        expirationDate
      }
      completed
    }
  }
`;

export const APPLICATION_CASE_QUERY = gql`
  query applicationCaseQuery($id: UUID!) {
    case(id: $id) {
      id
      caseStatus
      isEditableByCustomer
      isReadonlyForCaseworker
      completedStep
      lastStep
      hasPaid
      paymentTier
      attorneyReviewTerms
      attorneyReviewPurchased
      application {
        id
        dasherizedName
        name
        price
        reviewPrice
        applicationType {
          id
          name
        }
      }
      applicant {
        id
      }
      currentQueue {
        id
        name
        tabText
        customDirective
        queueType
        doneButtonText
      }
    }
  }
`;

export const APPLICANT_INFO_QUERY = gql`
  query applicantInfo($id: ID!) {
    applicant(id: $id) {
      id
      firstName
      lastName
      emailuserSet {
        connectPersonId
      }
    }
  }
`;

export const APPLICANT_INFO_CREATE_CASE_QUERY = gql`
  query applicantInfoCreateCase($id: ID!) {
    applicant(id: $id) {
      id
      firstName
      lastName
      allowedPermissions
      company {
        id
      }
    }
  }
`;

export const MAILING_INFO_QUERY = gql`
  query mailingInfo($id: UUID!) {
    case(id: $id) {
      id
      mailings {
        id
        created
        modified
        trackingNumber
        shippingProvider
        shippingType
        phoneNumber
        addressFullname
        addressMain
        addressCity
        addressState
        addressZip
        addressAttn
        fromPhoneNumber
        fromAddressFullname
        fromAddressMain
        fromAddressCity
        fromAddressState
        fromAddressZip
        parcelWeightOz
        hasCustomSender
        lock
        sentPdf
        notes
        trackingUrl
        saturdayDelivery
        easypostShipment {
          id
          refundStatus
          shippingLabel {
            file
          }
        }
      }
    }
  }
`;

export const APPLICANT_DETAIL_QUERY = gql`
  query applicantDetail($id: ID!) {
    applicant(id: $id) {
      ...ApplicantParts
      previewSections {
        name
        sectionType
        renderedHtml
      }
      previewFields {
        sensitiveField
        fieldName
        fieldValue
        section
        fieldVar
        fieldType
        dataType
        preserveWhitespace
      }
      allowedPermissions
      company {
        id
        created
        modified
        name
        logo
        companyLogo
        permissionGroupId
        applicantCount
        applicationcaseCount
        recruiterPassword
      }
      emailuserSet {
        ...UserParts
      }
      emailuser {
        ...UserParts
      }
      receipts {
        ...ReceiptParts
      }
    }
  }
  ${APPLICANT_PARTS}
  ${USER_PARTS}
  ${RECEIPT_PARTS}
`;

export const APPLICANT_DETAIL_CASES_QUERY = gql`
  query applicantDetailCases(
    $id: ID!
    $isInProgress: Boolean
    $isCompleted: Boolean
    $isClosed: Boolean
    $isOpen: Boolean
    $isOnHold: Boolean
    $isWorkingOffline: Boolean
  ) {
    applicant(id: $id) {
      id
      currentApplicationCaseId
      allCases(
        isInProgress: $isInProgress
        isCompleted: $isCompleted
        isClosed: $isClosed
        isOpen: $isOpen
        isOnHold: $isOnHold
        isWorkingOffline: $isWorkingOffline
      ) {
        ... on ApplicationCaseType {
          id
          created
          caseCode
          highLevelMarker
          caseStatus
          outcomeStatus
          hasPaid
          isRefundedCase
          application {
            applicationType {
              id
              name
            }
          }
          casemarkerSet {
            id
            name
            startedBy
            finishedBy
            startedDate
            finishedDate
            deadline
            action
            actionButtonData
            actionData
            level
            isMilestone
            assignee {
              id
              firstName
              lastName
              role
              email
            }
          }
        }
        ... on ConnectCaseModelType {
          created
          modified
          connectCaseId
          mappedStatus
          connectType
          imported
          connectApplicant {
            firstName
            lastName
            connectId
          }
        }
      }
    }
  }
`;

export const UPDATE_CONNECT_CASES_MUTATION = gql`
  mutation updateConnectCases($applicantId: String) {
    updateConnectCases(applicantId: $applicantId) {
      cases {
        created
        modified
        connectCaseId
        mappedStatus
        connectType
        imported
      }
    }
  }
`;

export const APPLICANT_DETAIL_DOCS_QUERY = gql`
  query applicantDetailDocs($id: ID!) {
    applicant(id: $id) {
      id
      uploadeddocumentSet {
        ...UploadedDocumentParts
        applicant {
          id
        }
        component {
          id
          name
        }
      }
    }
  }
  ${UPLOADED_DOCUMENT_PARTS}
`;

export const LIST_ROLES = gql`
  query roles($groupType: String) {
    managerRoles(groupType: $groupType) {
      ...RoleParts
    }
  }
  ${ROLE_PARTS}
`;

export const ADD_APPLICANT_TO_BUSINESS_UNIT_MUTATION = gql`
  mutation addApplicantToBusinessUnit($applicantId: ID!, $permissionGroupId: UUID!) {
    addApplicantToGroup(applicantId: $applicantId, permissionGroupId: $permissionGroupId) {
      businessUnit {
        id
        applicants {
          ...ApplicantParts
        }
      }
    }
  }
  ${APPLICANT_PARTS}
`;

export const APPLICANT_DETAIL_NOTES_QUERY = gql`
  query applicantDetailNotes($id: ID!) {
    applicant(id: $id) {
      id
      noteSet {
        ...NoteParts
      }
    }
  }
  ${NOTE_PARTS}
`;

export const APPLICANT_DETAIL_FLAGS_QUERY = gql`
  query applicantDetailFlags($id: ID!) {
    applicant(id: $id) {
      id
      flags {
        user {
          email
        }
        flag {
          id
          displayName
          name
        }
        active
        created
        modified
      }
    }
  }
`;

export const SET_APPLICANT_FLAG_MUTATION = gql`
  mutation setApplicantFlag($applicantId: ID!, $flagId: ID!, $active: Boolean!) {
    setApplicantFlag(applicantId: $applicantId, flagId: $flagId, active: $active) {
      record {
        active
      }
    }
  }
`;

export const SET_USER_FLAG_MUTATION = gql`
  mutation setUserFlag($userId: UUID!, $flagId: Int!, $active: Boolean!) {
    setUserFlag(userId: $userId, flagId: $flagId, active: $active) {
      record {
        active
      }
    }
  }
`;

export const FLAG_BASIC_QUERY = gql`
  query flags {
    flags {
      id
      name
      displayName
      active
    }
  }
`;

export const ALL_FLAGS_QUERY = gql`
  query allFlags {
    flags {
      id
      name
      displayName
      active
      activeUsers
      controlUsers
    }
  }
`;

export const FLAG_DETAIL_QUERY = gql`
  query flagDetail($flagId: Int!) {
    flag(flagId: $flagId) {
      id
      name
      displayName
      activeUsers
      controlUsers
      dataSummary {
        name
        testGroup {
          name
          value
        }
        controlGroup {
          name
          value
        }
      }
    }
  }
`;

export const FLAG_RECORDS_QUERY = gql`
  query flagRecords($flagId: Int!) {
    flag(flagId: $flagId) {
      id
      name
      percent
      displayName
      activeUsers
      controlUsers
      records {
        user {
          id
          email
        }
        active
        modified
      }
    }
  }
`;

export const CREATE_COMPANY_MUTATION = gql`
  mutation createCompany($description: String!, $name: String!) {
    createCompany(description: $description, name: $name) {
      company {
        ...CompanyParts
      }
    }
  }
  ${COMPANY_PARTS}
`;

export const CREATE_CASE_MUTATION = gql`
  mutation createCase(
    $applicantId: ID!
    $applicationTypeId: ID!
    $assignedParalegalId: UUID
    $assignedAttorneyId: UUID
    $assignedCompanyContactId: UUID
    $primaryBusinessUnitId: UUID
    $assignees: [CaseAssignmentsInput]
    $deadlineDate: DateTime
    $deadlineStatus: String
    $ssqId: ID
    $ssqData: GenericObjectField
  ) {
    createCase(
      data: {
        applicantId: $applicantId
        applicationTypeId: $applicationTypeId
        assignedParalegalId: $assignedParalegalId
        assignedAttorneyId: $assignedAttorneyId
        assignedCompanyContactId: $assignedCompanyContactId
        assignees: $assignees
        primaryBusinessUnitId: $primaryBusinessUnitId
        deadlineDate: $deadlineDate
        deadlineStatus: $deadlineStatus
        ssqId: $ssqId
        ssqData: $ssqData
      }
    ) {
      case {
        ...CaseParts
      }
      warningMsg
    }
  }
  ${CASE_PARTS}
`;

export const COMPANY_AVAILABLE_MANAGERS = gql`
  query companyAvailableManagers($companyId: ID) {
    company(id: $companyId) {
      id
      availableParalegals {
        ...ManagerMembershipParts
      }
      availableAttorneys {
        ...ManagerMembershipParts
      }
    }
  }
  ${MANAGER_MEMBERSHIP_PARTS}
`;

export const NEW_CASE_OPTIONS = gql`
  query newCaseOptions($applicantId: ID!, $applicationId: ID, $companyId: ID, $unitId: UUID) {
    applicant(id: $applicantId) {
      id
      newCaseOptions(applicationId: $applicationId, companyId: $companyId, unitId: $unitId) {
        availableBusinessUnits {
          id
          name
          unitOwner {
            ...UserParts
          }
        }
        availableAssignments {
          caseRoleType {
            name
            descriptiveName
          }
          availableManagers {
            ...ManagerMembershipParts
          }
        }
      }
    }
  }
  ${USER_PARTS}
  ${MANAGER_MEMBERSHIP_PARTS}
`;

export const NEW_CASE_CREATION_SSQ = gql`
  query newCaseCreationSSQ($applicantId: ID!, $companyId: ID, $applicationId: ID) {
    applicant(id: $applicantId) {
      id
      newCaseOptions(companyId: $companyId, applicationId: $applicationId) {
        newCaseQuestionnaire {
          id
          ssq {
            id
          }
          currentScreen {
            id
            subjectName
            savedAt
            markup
            activeCategory
            categories
            context
            screenId
            start
            end
            disableNext
          }
        }
      }
    }
  }
`;

export const QUEUE_TASKS_QUERY = gql`
  query queueTasksQuery(
    $isUpcomingList: Boolean
    $numPages: Int
    $page: Int
    $pageSize: Int
    $sortColumn: String
    $sortDir: String
  ) {
    queueTasksPaginated(
      isUpcomingList: $isUpcomingList
      numPages: $numPages
      page: $page
      pageSize: $pageSize
      sortColumn: $sortColumn
      sortDir: $sortDir
    ) {
      page
      numPages
      hasNext
      hasPrev
      total
      queueAssociations {
        id
        applicationCase {
          id
          application {
            id
            name
          }
          applicant {
            id
            emailuser {
              id
              email
            }
          }
        }
        applicationQueue {
          id
          displayName
        }
        created
      }
    }
  }
`;

export const MAILING_MUTATION = gql`
  mutation Mailing(
    $applicationCase: UUID!
    $trackingNumber: String!
    $shippingProvider: String!
    $shippingType: String!
    $phoneNumber: String!
    $addressFullname: String!
    $addressMain: String!
    $addressCity: String!
    $addressState: String!
    $addressZip: String!
    $addressAttn: String
    $useEasyPost: Boolean!
    $lock: Boolean
    $notes: String
    $saturdayDelivery: Boolean
    $fromPhoneNumber: String
    $fromAddressFullname: String
    $fromAddressMain: String
    $fromAddressCity: String
    $fromAddressState: String
    $fromAddressZip: String
    $parcelWeightOz: Decimal
  ) {
    mailing(
      applicationCase: $applicationCase
      trackingNumber: $trackingNumber
      shippingProvider: $shippingProvider
      shippingType: $shippingType
      phoneNumber: $phoneNumber
      addressFullname: $addressFullname
      addressMain: $addressMain
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      addressAttn: $addressAttn
      useEasyPost: $useEasyPost
      lock: $lock
      notes: $notes
      saturdayDelivery: $saturdayDelivery
      fromPhoneNumber: $fromPhoneNumber
      fromAddressFullname: $fromAddressFullname
      fromAddressMain: $fromAddressMain
      fromAddressCity: $fromAddressCity
      fromAddressState: $fromAddressState
      fromAddressZip: $fromAddressZip
      parcelWeightOz: $parcelWeightOz
    ) {
      mailing {
        id
        created
        modified
        trackingNumber
        shippingProvider
        shippingType
        phoneNumber
        addressFullname
        addressMain
        addressCity
        addressState
        addressZip
        fromPhoneNumber
        fromAddressFullname
        fromAddressMain
        fromAddressCity
        fromAddressState
        fromAddressZip
        parcelWeightOz
        hasCustomSender
        lock
        notes
        saturdayDelivery
      }
    }
  }
`;

export const MAILING_REFUND_MUTATION = gql`
  mutation mailingRefund($mailingId: ID!) {
    mailingRefund(mailingId: $mailingId) {
      mailing {
        id
        created
        modified
        trackingNumber
        shippingProvider
        shippingType
        phoneNumber
        addressFullname
        addressMain
        addressCity
        addressState
        addressZip
        lock
        sentPdf
        notes
        trackingUrl
        saturdayDelivery
        easypostShipment {
          id
          refundStatus
          shippingLabel {
            file
          }
        }
      }
    }
  }
`;

export const FILING_ADDRESSES_QUERY = gql`
  query FilingAddresses($applicationCase: UUID!) {
    filingAddresses(applicationCase: $applicationCase) {
      slugId
      name
      attn
      addressFullname
      phoneNumber
      addressMain
      addressCity
      addressState
      addressZip
    }
    suggestedFilingAddress(applicationCase: $applicationCase) {
      slugId
    }
  }
`;

export const GET_EASYPOST_ADDRESS = gql`
  query GetEasypostAddress($street1: String!, $city: String!, $state: String!, $zip: String!, $country: String!) {
    easypostAddress(street1: $street1, city: $city, state: $state, zip: $zip, country: $country) {
      street1
      street2
      city
      state
      zip
      country
    }
  }
`;

export const CASE_INTERCOM_LINK_QUERY = gql`
  query caseIntercomLink($caseId: UUID!) {
    case(id: $caseId) {
      id
      applicant {
        id
        emailuser {
          id
          intercomUserLink
        }
      }
    }
  }
`;

export const APPLICANT_INTERCOM_LINK_QUERY = gql`
  query applicantIntercomLink($applicantId: ID!) {
    applicant(id: $applicantId) {
      id
      emailuser {
        id
        intercomUserLink
      }
    }
  }
`;

export const PASSWORD_REQUIREMENTS_QUERY = gql`
  query passwordRequirements($passwordText: String) {
    passwordValidationRequirements(passwordCheck: $passwordText) {
      helpText
      isValid
      validationError
    }
  }
`;

export const APPLICANT_RELATIONSHIP_QUERY = gql`
  query applicantRelationshipQuery($applicantId: ID, $reltypeIds: [ID]) {
    applicantRelationship(applicantId: $applicantId, reltypeIds: $reltypeIds) {
      id
      firstName
      lastName
      type {
        id
        type
      }
      applicant {
        id
        firstName
        lastName
      }
    }
  }
`;

export const QUESTIONNAIRE_RESPONSES_QUERY = gql`
  query questionnaireResponsesQuery($questionnaireIds: [ID]) {
    questionnaireResponses(questionnaireIds: $questionnaireIds) {
      id
      xmlQuestionnaire {
        id
        file
        name
      }
    }
  }
`;

export const GENERATED_PDFS_QUERY = gql`
  query generatedPdfs($caseId: UUID) {
    generatedPdfs(caseId: $caseId) {
      id
      created
      pdf
      userStatus
      attachmentPdf
      adminStatus
      data
      componentIds
      componentSet
      generatedTime
      fileSize
      componentData
      reviewedByAttorney
    }
  }
`;

export const QUESTIONNAIRE_RESPONSE_QUERY = gql`
  query questionnaireResponseQuery($respID: UUID!) {
    questionnaireResponse(id: $respID) {
      id
      created
      modified
      screenHistory
      onHold
      locked
      instanceRepeatKey
      completedAt
      tabqId
      applicant {
        id
        firstName
        lastName
        fullName
        company {
          id
          name
        }
      }
      applicationCase {
        id
        attorneyReviewTerms
        attorneyReviewPurchased
        application {
          id
          name
          applicationType {
            id
            name
          }
        }
        tiersForPurchase {
          id
        }
      }
      company {
        id
      }
      businessEntity {
        id
        company {
          id
        }
      }
      xmlQuestionnaire {
        id
        file
        name
      }
    }
  }
`;
export const RESET_Q_RESPONSE_HISTORY_MUTATION = gql`
  mutation resetQuestionnaireResponseHistory($questionnaireResponseId: UUID) {
    resetQuestionnaireResponseHistory(questionnaireResponseId: $questionnaireResponseId) {
      questionnaireResponse {
        id
        created
        modified
        screenHistory
        onHold
        locked
        tabqId
        instanceRepeatKey
        completedAt
        applicant {
          id
          firstName
          lastName
          fullName
          company {
            id
            name
          }
        }
        applicationCase {
          id
          attorneyReviewTerms
          attorneyReviewPurchased
          application {
            id
            name
            applicationType {
              id
              name
            }
          }
          tiersForPurchase {
            id
          }
        }
        company {
          id
        }
        xmlQuestionnaire {
          id
          file
          name
        }
      }
    }
  }
`;

export const CREATE_QUESTIONNAIRE_TOKEN_MUTATION = gql`
  mutation createQuestionnaireToken($email: String!, $res_id: UUID!) {
    createQuestionnaireToken(email: $email, questionnaireRespId: $res_id) {
      token {
        id
        expirationDate
      }
    }
  }
`;

export const NEW_APPLICANT_QUESTIONNAIRE_QUERY = gql`
  query newApplicantQuestionnaire($companyId: ID!) {
    company(id: $companyId) {
      id
      newApplicantOptions {
        newApplicantQuestionnaire {
          id
          ssq {
            id
          }
          currentScreen {
            id
            subjectName
            savedAt
            markup
            activeCategory
            categories
            context
            screenId
            start
            end
            disableNext
          }
        }
      }
    }
  }
`;

export const EDIT_APPLICANT_QUESTIONNAIRE_QUERY = gql`
  query editApplicantQuestionnaire($applicantId: ID!) {
    applicant(id: $applicantId) {
      id
      editApplicantQuestionnaire {
        id
        ssq {
          id
        }
        currentScreen(applicantId: $applicantId) {
          id
          subjectName
          savedAt
          markup
          activeCategory
          categories
          context
          screenId
          start
          end
          disableNext
        }
      }
    }
  }
`;

export const EDIT_APPLICANT_QUESTIONNAIRE_MUTATION = gql`
  mutation editApplicantQuestionnaire($applicantId: ID!, $ssqId: ID!, $data: GenericObjectField) {
    editApplicantQuestionnaire(applicantId: $applicantId, ssqId: $ssqId, data: $data) {
      applicant {
        id
      }
    }
  }
`;

export const RUN_CASE_TICK_MUTATION = gql`
  mutation runCaseTick($caseId: UUID!) {
    runCaseTick(caseId: $caseId) {
      case {
        id
      }
    }
  }
`;

export const CREATE_GRID_ROW_MUTATION = gql`
  mutation createGridRow($responseId: UUID!, $gridRowInfo: GenericObjectField) {
    createGridRow(responseId: $responseId, gridRowInfo: $gridRowInfo) {
      savedContext
      success
    }
  }
`;

export const UPDATE_GRID_ROW_MUTATION = gql`
  mutation updateGridRow($responseId: UUID!, $dataToUpdate: GenericObjectField, $rowNumber: Int!) {
    updateGridRow(responseId: $responseId, dataToUpdate: $dataToUpdate, rowNumber: $rowNumber) {
      success
      updatedContext
    }
  }
`;

export const DELETE_GRID_ROW_MUTATION = gql`
  mutation deleteGridRow($responseId: UUID!, $dataToDelete: GenericObjectField, $rowNumber: Int!) {
    deleteGridRow(responseId: $responseId, dataToDelete: $dataToDelete, rowNumber: $rowNumber) {
      success
    }
  }
`;

export const QUESTIONNAIRE_NEXT_SCREEN_MUTATION = gql`
  mutation nextScreen($responseId: UUID!, $data: GenericObjectField, $viewOnly: Boolean) {
    nextScreen(responseId: $responseId, data: $data, viewOnly: $viewOnly) {
      nextScreen {
        id
        subjectName
        savedAt
        markup
        activeCategory
        categories
        context
        screenId
        screenTitle
        start
        end
        disableNext
        showNextSectionSkip
      }
    }
  }
`;

export const QUESTIONNAIRE_NEXT_SECTION_MUTATION = gql`
  mutation nextSection($responseId: UUID!, $data: GenericObjectField, $viewOnly: Boolean) {
    nextSection(responseId: $responseId, data: $data, viewOnly: $viewOnly) {
      nextScreen {
        id
        subjectName
        savedAt
        markup
        activeCategory
        categories
        context
        screenId
        screenTitle
        start
        end
        disableNext
        showNextSectionSkip
      }
    }
  }
`;

export const QUESTIONNAIRE_CURRENT_SCREEN_MUTATION = gql`
  mutation currentScreen($responseId: UUID!) {
    currentScreen(responseId: $responseId) {
      currentScreen {
        id
        subjectName
        savedAt
        markup
        activeCategory
        categories
        context
        screenId
        screenTitle
        start
        end
        disableNext
        showNextSectionSkip
      }
    }
  }
`;

export const QUESTIONNAIRE_PREVIOUS_SCREEN_MUTATION = gql`
  mutation previousScreen($responseId: UUID!) {
    previousScreen(responseId: $responseId) {
      previousScreen {
        id
        subjectName
        savedAt
        markup
        activeCategory
        categories
        context
        screenId
        screenTitle
        start
        end
        disableNext
        showNextSectionSkip
      }
    }
  }
`;

export const APPLICATION_TIERS_QUERY = gql`
  query applicationTiersQuery($caseId: UUID!) {
    case(id: $caseId) {
      id
      tiersForPurchase {
        id
        name
        tierLevel
        platformPrice
        legalServicesPrice
        totalPrice
        modal
      }
    }
  }
`;

export const TIERS_FOR_DOWNGRADE_QUERY = gql`
  query tiersForDowngradeQuery($caseId: UUID!) {
    case(id: $caseId) {
      id
      paymentTier
      tiersForDowngrade {
        id
        name
        tierLevel
        platformPrice
        legalServicesPrice
        totalPrice
      }
    }
  }
`;

export const DOWNGRADE_CASE_TIER_MUTATION = gql`
  mutation downgradeCaseTier($caseId: UUID!, $newTierId: ID!, $reason: String!) {
    downgradeCaseTier(caseId: $caseId, newTierId: $newTierId, reason: $reason) {
      case {
        ...CaseParts
      }
    }
  }
  ${CASE_PARTS}
`;

export const APPLICATION_TIER_TYPES_QUERY = gql`
  query applicationTierTypesQuery {
    applicationTierTypes {
      value
      label
    }
  }
`;

export const PURCHASE_TYPES_QUERY = gql`
  query purchaseTypesQuery {
    purchaseTypes {
      value
      label
    }
  }
`;

export const APPLICATION_TYPES_QUERY = gql`
  query applicationTypesQuery($applicantId: ID) {
    applicationType(applicantId: $applicantId) {
      id
      active
      activeVersion {
        id
        minDeadlineWindow
        estimatedTimeline
      }
      description
      name
      queuePath {
        id
        name
      }
    }
  }
`;

export const APPLICATION_TYPE_NAMES_QUERY = gql`
  query applicationTypeNamesQuery {
    applicationType {
      id
      name
      slugId
    }
  }
`;

export const APPLICATION_LIST_QUERY = gql`
  query applicationListQuery {
    applicationList {
      id
      name
      useFnMarkerProgress
      applicationType {
        id
        name
        active
        governmentProcessingTimeDefault
        inboundCountryCode
      }
    }
  }
`;

export const GLOBAL_SEARCH_QUERY = gql`
  query globalSearch($term: String) {
    globalSearch(q: $term) {
      __typename
      ... on ApplicantType {
        id
        firstName
        lastName
        fullName
        applicantCode
      }
      ... on ApplicationCaseType {
        caseId
        created
        caseCode
        application {
          name
        }
        applicant {
          firstName
          lastName
        }
      }
    }
  }
`;

export const WIDGET_DATA_SET_SEARCH_QUERY = gql`
  query widgetDataSetSearchQuery(
    $setName: String!
    $search: String
    $searchFields: [String]
    $fields: [String]
    $page: Int
    $pageSize: Int
    $filterByValue: String
    $filterByVariable: String
  ) {
    widgetDataSetSearchPaginated(
      setName: $setName
      search: $search
      searchFields: $searchFields
      fields: $fields
      page: $page
      pageSize: $pageSize
      filterByValue: $filterByValue
      filterByVariable: $filterByVariable
    ) {
      result
      page
      hasNextPage
      hasPreviousPage
    }
  }
`;

export const ADD_CASE_RECEIPT_MUTATION = gql`
  mutation addCaseReceipt($caseId: UUID!, $receiptNumber: String) {
    addCaseReceipt(caseId: $caseId, receiptNumber: $receiptNumber) {
      caseStatusReceipt {
        updateHistory {
          priority
          scTitle
          messageContains
        }
        id
        receiptNumber
      }
    }
  }
`;

export const SELECT_RECEIPTS_QUERY = gql`
  query caseReceipts($caseId: UUID!) {
    case(id: $caseId) {
      id
      caseReceipts {
        id
        receiptNumber
        uscisMessage
        uscisStatus
        formType
        uscisModifiedDate
        updateHistory {
          id
          scTitle
        }
      }
    }
  }
`;

export const REMOVE_CASE_RECEIPT_MUTATION = gql`
  mutation removeCaseReceipt($caseId: UUID!, $receiptId: ID!) {
    removeCaseReceipt(caseId: $caseId, receiptId: $receiptId) {
      success
    }
  }
`;

export const RECEIPT_UPDATE_QUERY = gql`
  query receiptUpdate($caseId: UUID!) {
    case(id: $caseId) {
      id
      caseReceiptUpdates {
        receipt {
          receiptNumber
          uscisStatus
          uscisMessage
          formType
          uscisModifiedDate
        }
        update {
          id
          priority
          displaysAlone
          statusContains
          messageContains
          scTitle
          scMessage
          scEventName
          nextStepDelta
          nextStepRange
          nextStepFormat
          nextStepName
          finishDelta
          finishRange
          finishFormat
          video
        }
        nextStepDatestring
        finishDatestring
        updated
      }
    }
  }
`;

export const NEXT_QUEUE_MUTATION = gql`
  mutation nextQueue($appCaseId: UUID!, $contextQueue: String) {
    nextQueue(appCaseId: $appCaseId, contextQueue: $contextQueue) {
      nextQueue {
        adminName
        backButtonText
        tabText
        statusName
        status
        shortName
        queueType
        queuePriority
        componentReviewNext
        customDirective
        dataLocked
        displayName
        doneButtonText
        feedbackQueue {
          id
        }
        id
        name
        nextQueue {
          id
        }
        openIntercomWindow
        hasChat
        postReview
        componentReview {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_LOCALE = gql`
  mutation updateLocale($userId: UUID!, $locale: String) {
    updateLocale(userId: $userId, locale: $locale) {
      user {
        id
        locale
      }
    }
  }
`;

export const CASE_SNAPSHOT_BY_NAME = gql`
  query caseSnapshotByName($caseId: UUID!, $snapshotName: String!) {
    case(id: $caseId) {
      id
      caseSnapshot(snapshotName: $snapshotName) {
        id
        name
        generatedPdf {
          ...GeneratedPDFParts
        }
      }
    }
  }
  ${GENERATED_PDF_PARTS}
`;

export const BILL_CODES_QUERY = gql`
  query billCodesQuery {
    billCodes {
      name
      chargeCode
      description
    }
  }
`;

export const QUESTIONNAIRE_SECTION_SUMMARY = gql`
  query QuestionnaireSectionSummary($responseId: UUID!) {
    questionnaireSummary(responseId: $responseId) {
      sections {
        title
        screens {
          id
          title
          subtitle
        }
      }
    }
  }
`;

export const CREATE_QUESTIONNAIRE = gql`
  mutation CreateQuestionnaire($name: String!) {
    createQuestionnaire(name: $name) {
      questionnaire {
        slugId
      }
    }
  }
`;

export const CREATE_SCREEN_PLACEMENT = gql`
  mutation CreateScreenPlacement(
    $questionnaireSlugId: String!
    $screenSlugId: String!
    $order: Int!
    $nextScreenId: String
    $section: String
    $title: String
    $subtitle: String
  ) {
    createScreenPlacement(
      questionnaireSlugId: $questionnaireSlugId
      screenSlugId: $screenSlugId
      order: $order
      nextScreenId: $nextScreenId
      section: $section
      title: $title
      subtitle: $subtitle
    ) {
      screenPlacement {
        slugId
      }
    }
  }
`;

export const CREATE_SCREEN = gql`
  mutation CreateScreen($slugId: String!) {
    createScreen(slugId: $slugId) {
      screen {
        slugId
      }
    }
  }
`;

export const DELETE_SCREEN_PLACEMENT = gql`
  mutation deleteScreenPlacement($slugId: String!) {
    deleteScreenPlacement(slugId: $slugId) {
      result
    }
  }
`;

export const EDIT_SCREEN = gql`
  mutation EditScreen(
    $screenSlugId: String!
    $extraContext: String
    $moreInfo: String
    $nextInfo: String
    $xmlContent: String
  ) {
    editScreen(
      slugId: $screenSlugId
      extraContext: $extraContext
      moreInfo: $moreInfo
      nextInfo: $nextInfo
      xmlContent: $xmlContent
    ) {
      screen {
        slugId
      }
    }
  }
`;

export const QUESTIONNAIRE_SCREEN = gql`
  query QuestionnaireScreen($screenId: String!, $responseId: UUID!) {
    questionnaireScreen(screenId: $screenId, responseId: $responseId) {
      id
      subjectName
      savedAt
      markup
      activeCategory
      categories
      context
      screenId
      start
      end
      disableNext
    }
  }
`;

export const APPLICATION_ASSIGNMENT_OPTIONS_QUERY = gql`
  query ApplicationAssignmentOptions($id: ID) {
    application(id: $id) {
      id
      applicationAssignmentOptions {
        id
        required
        caseRoleType {
          id
          name
        }
      }
    }
  }
`;

export const SEARCH_ASSIGNMENT_USERS = gql`
  query SearchAssignmentUsers($applicationId: ID!, $roleType: String!, $searchValue: String) {
    searchAssignmentUsers(applicationId: $applicationId, roleType: $roleType, searchValue: $searchValue) {
      id
      firstName
      lastName
      email
    }
  }
`;

export const QUESTIONNAIRE_WIDGET_SEARCH = gql`
  query QuestionnaireWidgetSearch($q: String, $caseType: String) {
    questionnaireWidgetSearch(q: $q, caseType: $caseType) {
      slugId
      widgetType
      attributes
      xmlString
      widgetTitle
      variables
    }
  }
`;

export const QUESTIONNAIRE_QUERY = gql`
  query q($slugId: String!) {
    questionnaire(slugId: $slugId) {
      slugId
    }
  }
`;

export const CASE_ROLE_TEMPLATES_QUERY = gql`
  query CaseRoleTemplatesQuery($companyId: ID) {
    company(id: $companyId) {
      caseRoleTemplates {
        id
        name
      }
    }
  }
`;

export const CASE_ROLE_TEMPLATE_QUERY = gql`
  query CaseRoleTemplateQuery($companyId: ID, $caseRoleTemplateId: String) {
    company(id: $companyId) {
      caseRoleTemplate(caseRoleTemplateId: $caseRoleTemplateId) {
        id
        name
        templateAssignments {
          userEmail
          userId
          userName
          assigneeType {
            id
            name
          }
        }
      }
    }
  }
`;

export const APPLICATION_MARKER_BASES = gql`
  query applicationMarkerBases($appId: ID!) {
    markerBases(applicationId: $appId, flat: true) {
      id
      name
      adminName
      description
      parent {
        id
        name
      }
      previousSibling {
        id
        name
      }
      specificApplication {
        id
        name
      }
      timeline
      sensitiveTimeline
      newAssignee {
        id
        name
      }
      action
      actionData
      actionButtonData
      startedBy
      startedByData
      finishedBy
      finishedByData
      visibilityRules
      creationRules
      affectsTimeline
      level
      markerNotificationCount
    }
  }
`;

export const CASE_ROLE_TYPE_QUERY = gql`
  query caseRoleTypesQuery {
    caseRoleTypes {
      id
      name
      adminName
    }
  }
`;

export const CREATE_MARKER_BASE = gql`
  mutation createMarkerBase(
    $name: String!
    $doPeerSiblingShift: Boolean
    $adminName: String
    $description: String
    $parentId: Int
    $previousSiblingId: Int
    $timeline: String
    $sensitiveTimeline: String
    $specificCompanyId: Int
    $specificApplicationId: Int
    $assignee: AssignOpts
    $newAssigneeId: Int
    $action: ActionOpts
    $actionData: GenericObjectField
    $actionButtonData: GenericObjectField
    $startedBy: StartedOpts
    $finishedBy: FinishedOpts
    $startedByData: GenericObjectField
    $finishedByData: GenericObjectField
    $visibilityRules: String
    $creationRules: String
    $affectsTimeline: Boolean
  ) {
    createMarkerBase(
      name: $name
      doPeerSiblingShift: $doPeerSiblingShift
      adminName: $adminName
      description: $description
      parentId: $parentId
      previousSiblingId: $previousSiblingId
      timeline: $timeline
      sensitiveTimeline: $sensitiveTimeline
      specificCompanyId: $specificCompanyId
      specificApplicationId: $specificApplicationId
      assignee: $assignee
      newAssigneeId: $newAssigneeId
      action: $action
      actionData: $actionData
      actionButtonData: $actionButtonData
      startedBy: $startedBy
      finishedBy: $finishedBy
      finishedByData: $finishedByData
      startedByData: $startedByData
      visibilityRules: $visibilityRules
      creationRules: $creationRules
      affectsTimeline: $affectsTimeline
    ) {
      markerBase {
        id
        name
        adminName
        description
        previousSibling {
          id
          name
        }
        timeline
        sensitiveTimeline
        specificCompany {
          id
          name
        }
        specificApplication {
          id
          name
        }
        assignee
        newAssignee {
          id
          name
        }
        action
        actionData
        actionButtonData
        startedBy
        startedByData
        finishedBy
        finishedByData
        visibilityRules
        creationRules
        affectsTimeline
        parent {
          id
          name
        }
      }
    }
  }
`;

export const UPDATE_MARKER_BASE = gql`
  mutation updateMarkerBase(
    $id: ID!
    $name: String
    $adminName: String
    $description: String
    $parent: ID
    $previousSibling: ID
    $timeline: String
    $specificApplication: ID
    $sensitiveTimeline: String
    $newAssignee: ID
    $action: ActionOpts
    $actionData: GenericObjectField
    $actionButtonData: GenericObjectField
    $startedBy: StartedOpts
    $finishedBy: FinishedOpts
    $startedByData: GenericObjectField
    $finishedByData: GenericObjectField
    $visibilityRules: String
    $creationRules: String
    $affectsTimeline: Boolean
  ) {
    updateMarkerBase(
      data: {
        id: $id
        name: $name
        adminName: $adminName
        description: $description
        parent: $parent
        previousSibling: $previousSibling
        timeline: $timeline
        specificApplication: $specificApplication
        sensitiveTimeline: $sensitiveTimeline
        newAssignee: $newAssignee
        action: $action
        actionData: $actionData
        actionButtonData: $actionButtonData
        startedBy: $startedBy
        finishedBy: $finishedBy
        startedByData: $startedByData
        finishedByData: $finishedByData
        visibilityRules: $visibilityRules
        creationRules: $creationRules
        affectsTimeline: $affectsTimeline
      }
    ) {
      markerBase {
        id
        specificApplication {
          id
          name
        }
      }
    }
  }
`;

export const DELETE_MARKER_BASE = gql`
  mutation deleteMarkerBase($id: ID!, $reassignNextSiblings: Boolean) {
    deleteMarkerBase(id: $id, reassignNextSiblings: $reassignNextSiblings) {
      result
    }
  }
`;

export const CREATE_APPLICATION_MUTATION = gql`
  mutation CreateApplication(
    $name: String!
    $active: Boolean
    $appTypeId: ID
    $appTypeName: String
    $createAppType: Boolean
    $govProcessTimeDefault: String
    $inboundCountryCode: String
    $useFnMarkerProgress: Boolean
    $visaType: String
  ) {
    createApplication(
      name: $name
      active: $active
      appTypeId: $appTypeId
      appTypeName: $appTypeName
      createAppType: $createAppType
      govProcessTimeDefault: $govProcessTimeDefault
      inboundCountryCode: $inboundCountryCode
      useFnMarkerProgress: $useFnMarkerProgress
      visaType: $visaType
    ) {
      application {
        id
        name
        useFnMarkerProgress
        applicationType {
          id
          name
          active
          inboundCountryCode
          governmentProcessingTimeDefault
          visaType
        }
      }
    }
  }
`;

export const EDIT_APPLICATION_MUTATION = gql`
  mutation EditApplication(
    $active: Boolean!
    $appId: ID!
    $appTypeName: String!
    $govProcessingTimeDefault: String!
    $inboundCountryCode: String!
    $name: String!
    $useFnMarkerProgress: Boolean!
    $visaType: String
  ) {
    editApplication(
      active: $active
      appId: $appId
      appTypeName: $appTypeName
      govProcessingTimeDefault: $govProcessingTimeDefault
      inboundCountryCode: $inboundCountryCode
      name: $name
      useFnMarkerProgress: $useFnMarkerProgress
      visaType: $visaType
    ) {
      application {
        id
        name
        useFnMarkerProgress
        applicationType {
          id
          name
          active
          inboundCountryCode
          governmentProcessingTimeDefault
          visaType
        }
      }
    }
  }
`;

export const COMPANY_APPLICATION_CASES = gql`
  query companyApplicationCases(
    $companyId: ID!
    $search: String
    $orderBy: String
    $companyApplicationCasesId: ID
    $last: Int
    $first: Int
    $after: String
    $before: String
    $offset: Int
  ) {
    companyApplicationCases(
      companyId: $companyId
      search: $search
      orderBy: $orderBy
      id: $companyApplicationCasesId
      last: $last
      first: $first
      after: $after
      before: $before
      offset: $offset
    ) {
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          caseSubType
          caseStatus
          employeeName
          applicationCaseType
          caseCode
          caseId
          id
          applicantId
        }
      }
    }
  }
`;

export const LOCK_QUESTIONNAIRE = gql`
  mutation LockQuestionnaire($qResponseId: UUID!) {
    lockQuestionnaire(qResponseId: $qResponseId) {
      qResponse {
        id
        locked
        created
      }
    }
  }
`;

export const UNLOCK_QUESTIONNAIRE = gql`
  mutation UnlockQuestionnaire($qResponseId: UUID!) {
    unlockQuestionnaire(qResponseId: $qResponseId) {
      qResponse {
        id
        locked
        created
      }
    }
  }
`;

export const JUMP_TO_SECTION_MUTATION = gql`
  mutation questionnaireJumpToSection($sectionName: String!, $responseId: UUID!) {
    jumpToSection(responseId: $responseId, sectionName: $sectionName) {
      questionnaireResponse {
        id
      }
    }
  }
`;

export const MY_TODOS_CONNECTION_QUERY = gql`
  query myTodosConnection(
    $search: String
    $orderBy: String
    $selectTaskFilterVar: String
    $selectedTeamMembers: [UUID]
    $markerFilterFields: [String]
    $selectedPods: [ID]
    $myTodosConnectionId: ID
    $last: Int
    $first: Int
    $after: String
    $before: String
    $offset: Int
    $workingOffline: Boolean
  ) {
    myTodosConnection(
      offset: $offset
      before: $before
      after: $after
      first: $first
      last: $last
      id: $myTodosConnectionId
      orderBy: $orderBy
      search: $search
      selectTaskFilterVar: $selectTaskFilterVar
      selectedTeamMembers: $selectedTeamMembers
      workingOffline: $workingOffline
      markerFilterFields: $markerFilterFields
      selectedPods: $selectedPods
    ) {
      totalCount
      pageInfo {
        startCursor
        hasPreviousPage
        endCursor
        hasNextPage
      }
      edges {
        cursor
        node {
          taskName
          startedDate
          markerId
          id
          deadline
          caseId
          caseStatus
          applicationCaseType
          caseAttorneyReviewUrgentAttention
          applicantName
          applicantId
          assigneeName
          questionnaireResponseId
          actionData
          serviceFulfillmentIcons
        }
      }
    }
  }
`;

export const APPLICATION_SERVICES_QUERY = gql`
  query ApplicationServices {
    applicationServices {
      description
      features
      id
      name
      displayName
      platformPrice
      legalServicesPrice
      totalPrice
      showOnDashboard
      slugName
      iconUrl
      crossSellLinks {
        id
        crossSellDescription
        service {
          id
          name
          displayName
          description
          features
          platformPrice
          legalServicesPrice
          totalPrice
          showOnDashboard
          slugName
          iconUrl
        }
      }
    }
  }
`;

export const DASHBOARD_APPLICATION_SERVICES_QUERY = gql`
  query DashboardApplicationServices {
    applicationServices(applyVisibilityRules: true) {
      description
      features
      id
      name
      displayName
      platformPrice
      legalServicesPrice
      totalPrice
      showOnDashboard
      slugName
      iconUrl
      crossSellLinks {
        id
        crossSellDescription
        service {
          id
          name
          displayName
          description
          features
          platformPrice
          legalServicesPrice
          totalPrice
          showOnDashboard
          slugName
          iconUrl
        }
      }
    }
  }
`;

export const ALL_CASE_TEAM_MEMBERS_QUERY = gql`
  query allCaseTeamMembers {
    allCaseTeamMembers {
      id
      caseTeam
      memberEmail
      memberUserId
    }
  }
`;

export const ALL_PODS_QUERY = gql`
  query allPods {
    allPods {
      id
      name
    }
  }
`;

export const ATTORNEY_REVIEW_TAGS = gql`
  query attorneyReviewTags($applicationId: ID!, $caseId: UUID!) {
    attorneyReviewTag(applicationId: $applicationId, caseId: $caseId) {
      applicationTags {
        slugId
        tagName
        description
        text
        reviewStep
      }
      caseTags {
        slugId
        tagName
        description
        text
        reviewStep
      }
    }
  }
`;

export const CREATE_PROMO_CODE_MUTATION = gql`
  mutation createPromoCode(
    $codeName: String!
    $promoCode: String!
    $pricePercentage: Int
    $dollarAmount: String
    $purchaseTypes: [String]
    $applicationRestriction: [String]
    $tierRestriction: [String]
    $serviceRestriction: [String]
    $expires: String
    $allowedUses: Int
    $emailPattern: String
  ) {
    createPromoCode(
      codeName: $codeName
      promoCode: $promoCode
      pricePercentage: $pricePercentage
      dollarAmount: $dollarAmount
      purchaseTypes: $purchaseTypes
      applicationRestriction: $applicationRestriction
      tierRestriction: $tierRestriction
      serviceRestriction: $serviceRestriction
      expires: $expires
      allowedUses: $allowedUses
      emailPattern: $emailPattern
    ) {
      success
      message
    }
  }
`;

export const ALL_VISA_BULLETINS = gql`
  query AllVisaBulletins {
    allVisaBulletins {
      allChargeabilityDate
      bulletinType
      chinaDate
      indiaDate
      mexicoDate
      modified
      philippinesDate
      visaCategory
    }
  }
`;

export const UPDATE_VISA_BULLETIN = gql`
  mutation UpdateVisaBulletin($bulletinType: String!, $updateData: JSONString!) {
    updateVisaBulletin(bulletinType: $bulletinType, updateData: $updateData) {
      success
    }
  }
`;

export const DELETE_APPLICATION_CASE = gql`
  mutation DeleteApplicationCase($caseId: UUID!) {
    deleteApplicationCase(caseId: $caseId) {
      success
    }
  }
`;

// Feedback Items
export const FEEDBACK_ITEM_PARTS = gql`
  fragment FeedbackItemParts on FeedbackItemType {
    id: slugId
    text
    urgency
    fnVerified
    cwVerified
    status
    reviewStep
    displayOrder
  }
`;

export const FEEDBACK_ITEMS_QUERY = gql`
  query feedbackItems($caseId: UUID!, $archived: Boolean, $complete: Boolean) {
    feedbackItems(caseId: $caseId, archived: $archived, complete: $complete) {
      ...FeedbackItemParts
    }
  }
  ${FEEDBACK_ITEM_PARTS}
`;

export const CREATE_FEEDBACK_ITEM_MUTATION = gql`
  mutation createFeedbackItem($caseId: UUID!, $text: String!, $reviewStep: String!, $urgency: String) {
    createFeedbackItem(caseId: $caseId, text: $text, reviewStep: $reviewStep, urgency: $urgency) {
      feedbackItem {
        ...FeedbackItemParts
      }
    }
  }
  ${FEEDBACK_ITEM_PARTS}
`;

export const UPDATE_FEEDBACK_ITEMS_MUTATION = gql`
  mutation updateFeedbackItems(
    $slugIds: [String]!
    $text: String!
    $urgency: String
    $cwVerified: Boolean
    $fnVerified: Boolean
    $archive: Boolean
  ) {
    updateFeedbackItems(
      slugIds: $slugIds
      text: $text
      urgency: $urgency
      cwVerified: $cwVerified
      fnVerified: $fnVerified
      archive: $archive
    ) {
      feedbackItems {
        ...FeedbackItemParts
      }
    }
  }
  ${FEEDBACK_ITEM_PARTS}
`;

export const DELETE_FEEDBACK_ITEMS_MUTATION = gql`
  mutation deleteFeedbackItems($slugIds: [String]!) {
    deleteFeedbackItems(slugIds: $slugIds) {
      deletedIds
    }
  }
`;

export const REORDER_FEEDBACK_ITEMS_MUTATION = gql`
  mutation reorderFeedbackItems($caseId: UUID!, $items: [ReorderInput]!) {
    reorderFeedbackItems(caseId: $caseId, items: $items) {
      feedbackItems {
        ...FeedbackItemParts
      }
    }
  }
  ${FEEDBACK_ITEM_PARTS}
`;

export const ACTIVE_CALENDLY_EVENTS = gql`
  query ActiveCalendlyEvents($leadsOnlyEvent: Boolean) {
    activeCalendlyEvents(leadsOnlyEvent: $leadsOnlyEvent) {
      eventName
      slugId
    }
  }
`;

export const CREATE_CALENDLY_LINK = gql`
  mutation CreateCalendlyLink($eventTypeSlugId: String!, $leadEmail: String, $caseId: UUID) {
    createCalendlyLink(eventTypeSlugId: $eventTypeSlugId, leadEmail: $leadEmail, caseId: $caseId) {
      calendlyLink {
        id
        created
        eventBookingLink
        leadEmail
        eventType {
          eventName
          slugId
        }
      }
    }
  }
`;

export const CASE_CALENDLY_LINKS = gql`
  query CaseCalendlyLinks($caseId: UUID!) {
    caseCalendlyLinks(caseId: $caseId) {
      id
      created
      eventBookingLink
      isHidden
      eventType {
        eventName
        slugId
      }
    }
  }
`;

export const HIDE_CALENDLY_LINK = gql`
  mutation HideCalendlyLink($id: UUID!, $isHidden: Boolean!) {
    hideCalendlyLink(id: $id, isHidden: $isHidden) {
      calendlyLink {
        id
        isHidden
      }
    }
  }
`;

export const MY_CALENDLY_LINKS = gql`
  query MyCalendlyLinks {
    myCalendlyLinks {
      caseId
      caseName
      calendlyLinks {
        id
        created
        eventBookingLink
        eventType {
          eventName
          slugId
        }
      }
    }
  }
`;
